import { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { useParams, Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useClient } from '../../hooks/useClients';
import { useUsers } from '../../hooks/useUsers';

import { useConsentStatus } from '../../hooks/useConsentStatus';
import { useSecuritySnapshot } from '../../hooks/useSecuritySnapshot';
import { useClientSyncData } from '../../hooks/useClientSyncData';
import { syncClient, getBillingAuthUrl, updateClient, getClientGDAPStatus, ClientGDAPStatusResponse, smartSync } from '../../services/api';
import { getSubscriptionCategory } from '../../types';
import StatusBadge from '../common/StatusBadge';
import ClientForm from './ClientForm';
import UserList from '../Users/UserList';
import GroupList from '../Groups/GroupList';
import ConsentWizard from '../Consent/ConsentWizard';
import SyncStatusHeader from '../Sync/SyncStatusHeader';
import { format, parseISO, differenceInDays, differenceInHours } from 'date-fns';
import {
  ArrowLeft,
  Pencil,
  CreditCard,
  Calendar,
  RefreshCw,
  ChevronDown,
  AlertTriangle,
  ShoppingCart,
  Cloud,
  DollarSign,
  Users,
  UsersRound,
  ShieldCheck,
  CheckCircle,
  ExternalLink,
  Mail,
  FileText,
  UserPlus,
  Link2,
  XCircle,
  Globe,
  Shield,
  Clock,
  ChevronRight,
} from 'lucide-react';
import MailboxesTab from '../Mailboxes/MailboxesTab';
import SharePointSiteList from '../SharePoint/SharePointSiteList';
import ClientSecurityDashboard from '../Security/ClientSecurityDashboard';
import CollectionCoverage from '../Security/CollectionCoverage';
import HelpTooltip from '../common/HelpTooltip';
import LicenseAssignmentReport from '../Licenses/LicenseAssignmentReport';
import BulkLicenseAssignModal from '../Licenses/BulkLicenseAssignModal';
import EmailAuthResults from '../EmailAuth/EmailAuthResults';
import ClientEmailHealth from '../EmailHealth/ClientEmailHealth';
import SpamSamplesPanel from '../EmailHealth/SpamSamplesPanel';
import ClientDmarc from '../Dmarc/ClientDmarc';
import GoogleOAuthApps from '../Google/GoogleOAuthApps';
import GoogleMobileDevices from '../Google/GoogleMobileDevices';
import GoogleAdminAudit from '../Google/GoogleAdminAudit';

// Helper: Check if a string looks like a GUID (orphaned/unknown product)
const isGuidProductName = (name: string): boolean => {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(name?.trim() || '');
};

// SKU name aliases: maps raw MS SKU names to canonical display names
// This ensures MS Direct subscriptions match Pax8 subscriptions
const SKU_ALIASES: Record<string, string> = {
  // Storage
  'sharepointstorage': 'office 365 extra file storage',
  'sharepoint_s_developer': 'sharepoint online for developer',
  'onedrive_basic': 'onedrive for business basic',
  'onedrive_basic_p2': 'onedrive for business plan 2',
  // Azure AD SKU aliases (internal MS SKU names to display names)
  // NOTE: Microsoft Entra ID P1/P2 and Azure AD Premium P1/P2 are DIFFERENT products!
  // Azure AD Premium / Microsoft Entra ID - same underlying SKU, different names
  // Microsoft renamed Azure AD to Entra ID - map all variations to the modern name
  'aad_premium': 'microsoft entra id p1',
  'aad_premium_p2': 'microsoft entra id p2',
  'aadpremium': 'microsoft entra id p1',
  'aadpremiump2': 'microsoft entra id p2',
  'azureadpremiump1': 'microsoft entra id p1',
  'azureadpremiump2': 'microsoft entra id p2',
  'azureactivedirectorypremiump1': 'microsoft entra id p1',
  'azureactivedirectorypremiump2': 'microsoft entra id p2',
  'entraid p1': 'microsoft entra id p1',
  'entraid p2': 'microsoft entra id p2',
  'entraidp1': 'microsoft entra id p1',
  'entraidp2': 'microsoft entra id p2',
  'aad_basic': 'azure active directory basic',
  'aad_smb': 'azure active directory',
  // Enterprise Mobility
  'ems': 'enterprise mobility security e3',
  'emspremium': 'enterprise mobility security e5',
  // Power Platform (often free/viral)
  'power_bi_standard': 'power bi free',
  'flow_free': 'microsoft power automate free',
  'powerapps_viral': 'power apps free',
  // Common suites
  'enterprisepack': 'office 365 e3',
  'enterprisepremium': 'office 365 e5',
  'spb': 'microsoft 365 business premium',
  // Exchange
  'exchangestandard': 'exchange online plan 1',
  'exchangeenterprise': 'exchange online plan 2',
  'exchangearchive': 'exchange online archiving',
  // Teams
  'mcoev': 'microsoft teams phone standard',
  'teams_exploratory': 'microsoft teams exploratory',
  // Intune
  'intune_a': 'microsoft intune',
  'intune_a_d': 'microsoft intune device',
};

// Helper: Normalize product name for matching
// Also resolves SKU name aliases to ensure MS Direct and Pax8 names match
// NOTE: Microsoft Entra ID P2 and Azure AD Premium P2 are DIFFERENT products - don't merge them!
const normalizeProductName = (name: string): string => {
  let normalized = name
    .replace(/\s*\[New Commerce Experience\]/gi, '')
    .replace(/\s*\(NCE\)/gi, '')
    .replace(/\s*NCE$/gi, '')
    .trim()
    .toLowerCase();

  // Remove extra whitespace and non-alphanumeric for alias lookup
  const aliasKey = normalized.replace(/[^a-z0-9]/g, '');

  // Check if there's an alias for this SKU name
  if (SKU_ALIASES[aliasKey]) {
    normalized = SKU_ALIASES[aliasKey];
  }

  // Also check the original name (in case it's a raw SKU like "SHAREPOINTSTORAGE")
  const rawKey = name.trim().toLowerCase();
  if (SKU_ALIASES[rawKey]) {
    normalized = SKU_ALIASES[rawKey];
  }

  return normalized;
};

// Check if a product name indicates a Microsoft license (vs third-party like Dropsuite)
const isMicrosoftProduct = (productName: string): boolean => {
  const msKeywords = [
    'microsoft', 'office 365', 'office365', 'm365', 'microsoft 365',
    'exchange', 'sharepoint', 'teams', 'onedrive', 'azure', 'entra',
    'intune', 'defender', 'power bi', 'power apps', 'power automate',
    'dynamics', 'visio', 'project', 'windows', 'ems', 'enterprise mobility',
    'aad', 'flow', 'yammer', 'sway', 'planner', 'stream', 'whiteboard',
    'copilot', 'viva', 'bookings', 'forms', 'lists', 'todo'
  ];
  const lower = productName.toLowerCase();
  return msKeywords.some(kw => lower.includes(kw));
};

// Grouped subscription type
interface GroupedProduct {
  normalizedName: string;
  displayName: string;
  msTenantQty: number;        // What they have in MS tenant
  msTenantAssigned: number;   // How many assigned to users
  pax8Qty: number;            // What's covered by Pax8
  pax8AutoRenewQty: number;   // Pax8 with auto-renew ON
  gap: number;                // MS - Pax8 (unmatched)
  coverageStatus: 'covered' | 'partial' | 'unmatched' | 'ignored' | 'tenant-level' | 'pax8-only';
  isTenantLevel: boolean;     // Licenses applied to tenant, not users (e.g., SharePoint Storage)
  isIgnored: boolean;         // Marked as ignored (purchased from other vendor)
  isPax8Only: boolean;        // Pax8 product that isn't an MS license (e.g., Dropsuite)
  earliestRenewal: string | null;
  msSubscriptions: Array<{
    id: string;
    quantity: number;
    assignedQuantity: number | null;
    renewalDate: string | null;
    autoRenew: boolean;
    productName: string;
    msBillingStatus?: 'active' | 'warning' | 'suspended' | 'unknown';
    msNextLifecycleDate?: string | null;
    msCreatedDate?: string | null;
    billingLastChecked?: string | null;
    msSkuId?: string;  // MS SKU GUID for Graph API license assignment
  }>;
  pax8Subscriptions: Array<{ id: string; quantity: number; renewalDate: string | null; autoRenew: boolean; productName: string }>;
}

// SKUs that are truly tenant-level (not assigned to individual users)
// Note: Azure AD Premium CAN be assigned to users, so it's NOT tenant-level
const TENANT_LEVEL_SKUS = [
  'sharepointstorage',
  'office 365 extra file storage',
  'sharepoint online storage',
  // Other storage add-ons
  'onedrive_basic',
  'onedrive for business basic',
];

type TabType = 'subscriptions' | 'users' | 'groups' | 'security' | 'mailboxes' | 'sharepoint' | 'soc' | 'emailhealth' | 'dmarc' | 'google-oauth' | 'google-devices' | 'google-audit';

export default function ClientDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { client, loading, error, refetch } = useClient(id);
  const [showEditForm, setShowEditForm] = useState(false);
  const [showConsentWizard, setShowConsentWizard] = useState(false);
  const [syncingPax8, setSyncingPax8] = useState(false);
  const [syncingMS, setSyncingMS] = useState(false);
  const [verifyingBilling, setVerifyingBilling] = useState(false);
  const [syncError, setSyncError] = useState<string | null>(null);
  const [syncSuccess, setSyncSuccess] = useState<string | null>(null);
  const [showTrialFree, setShowTrialFree] = useState(false);
  const [billingDebugData, setBillingDebugData] = useState<Record<string, unknown> | null>(null);
  const [showBillingDebug, setShowBillingDebug] = useState(false);
  const [activeTab, setActiveTab] = useState<TabType>('subscriptions');
  const [coverageRefreshTrigger, setCoverageRefreshTrigger] = useState(0);
  const [showLicenseReport, setShowLicenseReport] = useState(false);
  const [showEmailAuth, setShowEmailAuth] = useState(false);
  const [bulkAssignModal, setBulkAssignModal] = useState<{
    isOpen: boolean;
    skuId: string;
    productName: string;
    available: number;
  } | null>(null);

  // Sidebar collapse state (persisted in localStorage)
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try {
      return localStorage.getItem('clientDetail.sidebarCollapsed') === 'true';
    } catch {
      return false;
    }
  });
  const toggleSidebar = useCallback(() => {
    setSidebarCollapsed(prev => {
      const next = !prev;
      try { localStorage.setItem('clientDetail.sidebarCollapsed', String(next)); } catch {}
      return next;
    });
  }, []);

  // Draggable sidebar edge tab Y position (persisted in localStorage)
  const [sidebarTabY, setSidebarTabY] = useState(() => {
    try {
      const saved = localStorage.getItem('clientDetail.sidebarTabY');
      return saved ? parseInt(saved, 10) : 300;
    } catch {
      return 300;
    }
  });
  const [isDraggingTab, setIsDraggingTab] = useState(false);
  const dragOffsetRef = useRef(0);
  const dragStartYRef = useRef(0);
  const didDragRef = useRef(false);
  const latestTabYRef = useRef(sidebarTabY);
  latestTabYRef.current = sidebarTabY;

  const handleTabMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingTab(true);
    dragOffsetRef.current = e.clientY - latestTabYRef.current;
    dragStartYRef.current = e.clientY;
    didDragRef.current = false;
  }, []);

  useEffect(() => {
    if (!isDraggingTab) return;
    const handleMouseMove = (e: MouseEvent) => {
      if (Math.abs(e.clientY - dragStartYRef.current) > 4) {
        didDragRef.current = true;
      }
      const newY = Math.max(60, Math.min(window.innerHeight - 100, e.clientY - dragOffsetRef.current));
      setSidebarTabY(newY);
    };
    const handleMouseUp = () => {
      setIsDraggingTab(false);
      try { localStorage.setItem('clientDetail.sidebarTabY', String(latestTabYRef.current)); } catch {}
      if (!didDragRef.current) {
        toggleSidebar();
      }
    };
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDraggingTab, toggleSidebar]);

  // GDAP relationship status
  const [gdapStatus, setGdapStatus] = useState<ClientGDAPStatusResponse | null>(null);
  const [gdapLoading, setGdapLoading] = useState(false);

  // Fetch users data
  const { users, userCount, loading: usersLoading, error: usersError, refetch: refetchUsers } = useUsers(id);

  // Groups data comes from cached sync data (syncData.groups)

  // Fetch consent status (for GDAP)
  const { status: consentStatus, loading: consentLoading, fetchStatus: fetchConsentStatus, refetch: refetchConsentStatus } = useConsentStatus(id);

  // Fetch security snapshot
  const { snapshot: securitySnapshot, loading: securityLoading, error: securityError, fetchSnapshot: fetchSecuritySnapshot, refetch: refetchSecurity } = useSecuritySnapshot(id);

  // Fetch cached sync data (users, groups, mailboxes, security from MS)
  const {
    syncData,
    syncing: msSyncing,
    staleness,
    error: msSyncError,
    triggerSync: triggerMSSync,
    refresh: refreshSyncData
  } = useClientSyncData(id || null);

  // Smart sync handler — replaces old MS sync for the header button
  const [smartSyncing, setSmartSyncing] = useState(false);
  const handleSmartSync = async () => {
    if (!id) return;
    setSmartSyncing(true);
    try {
      await smartSync(id);
      // Refresh sync data after smart sync completes
      refreshSyncData();
    } catch (err) {
      setSyncError(err instanceof Error ? err.message : 'Smart sync failed');
    } finally {
      setSmartSyncing(false);
    }
  };

  // Security refresh handler — triggers smart sync then refetches security snapshot
  const [securityRefreshing, setSecurityRefreshing] = useState(false);
  const handleRefreshSecurity = async () => {
    if (!id) return;
    setSecurityRefreshing(true);
    setSyncError(null);
    try {
      await smartSync(id);
      refreshSyncData();
      await refetchSecurity();
    } catch (err) {
      setSyncError(err instanceof Error ? err.message : 'Security refresh failed');
    } finally {
      setSecurityRefreshing(false);
    }
  };

  // Subscription refresh handler — triggers Pax8 + MS sync then refetches client
  const [subsRefreshing, setSubsRefreshing] = useState(false);
  const handleRefreshSubscriptions = async () => {
    if (!id) return;
    setSubsRefreshing(true);
    setSyncError(null);
    setSyncSuccess(null);
    try {
      const pax8Result = await syncClient(id);
      const msResult = await syncClient(id, 'ms');
      setSyncSuccess(`Subscriptions refreshed: Pax8 (${pax8Result.subscriptionsProcessed} subs), MS (${msResult.subscriptionsProcessed} subs)`);
      refetch();
    } catch (err) {
      setSyncError(err instanceof Error ? err.message : 'Subscription refresh failed');
    } finally {
      setSubsRefreshing(false);
    }
  };

  // Show sync errors as toast
  useEffect(() => {
    if (msSyncError) {
      setSyncError(`Sync failed: ${msSyncError}`);
    }
  }, [msSyncError]);

  // Auto-fetch consent status when client has a tenant ID
  useEffect(() => {
    if (client?.tenantId) {
      fetchConsentStatus();
    }
  }, [client?.tenantId, fetchConsentStatus]);

  // Auto-fetch GDAP relationship status
  useEffect(() => {
    if (id && client) {
      setGdapLoading(true);
      getClientGDAPStatus(id)
        .then(setGdapStatus)
        .catch(() => setGdapStatus(null))
        .finally(() => setGdapLoading(false));
    }
  }, [id, client?.tenantId]);

  // Auto-fetch security snapshot when security or users tab is selected
  // Don't retry on error - let user manually click "Try Again" to avoid infinite 404 loop
  useEffect(() => {
    if ((activeTab === 'security' || activeTab === 'users') && client?.tenantId && !securitySnapshot && !securityLoading && !securityError) {
      fetchSecuritySnapshot();
    }
  }, [activeTab, client?.tenantId, securitySnapshot, securityLoading, securityError, fetchSecuritySnapshot]);

  // Handle billing callback URL params
  useEffect(() => {
    const billingSuccess = searchParams.get('billingSuccess');
    const billingError = searchParams.get('billingError');

    if (billingSuccess) {
      setSyncSuccess(`Billing verified! Updated ${billingSuccess} subscription(s) with MS billing status.`);
      searchParams.delete('billingSuccess');
      setSearchParams(searchParams, { replace: true });
      refetch(); // Refresh to show new data
    }

    if (billingError) {
      setSyncError(`Billing verification failed: ${billingError}`);
      searchParams.delete('billingError');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams, refetch]);

  // Grouped view state
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [showOrphaned, setShowOrphaned] = useState(false);
  const [showPax8Only, setShowPax8Only] = useState(false);

  const toggleGroup = (groupName: string) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(groupName)) {
        next.delete(groupName);
      } else {
        next.add(groupName);
      }
      return next;
    });
  };

  // Toggle whether a license is ignored (purchased from other vendor)
  const toggleIgnoredLicense = async (normalizedName: string) => {
    if (!client || !id) return;

    const currentIgnored = client.ignoredLicenses || [];
    let newIgnored: string[];

    if (currentIgnored.includes(normalizedName)) {
      // Remove from ignored
      newIgnored = currentIgnored.filter(name => name !== normalizedName);
    } else {
      // Add to ignored
      newIgnored = [...currentIgnored, normalizedName];
    }

    try {
      await updateClient(id, { ignoredLicenses: newIgnored });
      refetch(); // Refresh client data
    } catch (err) {
      setSyncError(err instanceof Error ? err.message : 'Failed to update ignored licenses');
    }
  };

  const handleSyncPax8 = async () => {
    if (!id) return;
    setSyncingPax8(true);
    setSyncError(null);
    setSyncSuccess(null);
    try {
      const result = await syncClient(id);
      setSyncSuccess(`Pax8: Synced ${result.subscriptionsProcessed} subscriptions (${result.subscriptionsAdded} new, ${result.subscriptionsUpdated} updated)`);
      refetch();
    } catch (err) {
      setSyncError(err instanceof Error ? err.message : 'Failed to sync Pax8');
    } finally {
      setSyncingPax8(false);
    }
  };

  const handleSyncMS = async () => {
    if (!id) return;
    setSyncingMS(true);
    setSyncError(null);
    setSyncSuccess(null);
    try {
      const result = await syncClient(id, 'ms');
      setSyncSuccess(`MS: Synced ${result.subscriptionsProcessed} subscriptions (${result.subscriptionsAdded} new, ${result.subscriptionsUpdated} updated)`);
      refetch();
    } catch (err) {
      setSyncError(err instanceof Error ? err.message : 'Failed to sync MS licenses');
    } finally {
      setSyncingMS(false);
    }
  };

  // Open Pax8 portal for this client's subscriptions
  const handleOpenPax8Portal = () => {
    if (!client?.pax8CompanyId) {
      alert('No Pax8 Company ID configured for this client.\n\nPlease edit the client and add their Pax8 Company ID first.');
      return;
    }

    const confirmed = window.confirm(
      `OPEN PAX8 PORTAL FOR: ${client.companyName}\n\n` +
      `This will open the Pax8 Partner Portal in a new tab.\n\n` +
      `INSTRUCTIONS:\n` +
      `1. Sign in with your Pax8 partner credentials (if not already signed in)\n` +
      `2. You'll be taken directly to this client's subscriptions page\n` +
      `3. From there you can view/modify their Pax8 subscriptions\n\n` +
      `Click OK to open Pax8 Portal.`
    );

    if (confirmed) {
      window.open(`https://app.pax8.com/account/clients/${client.pax8CompanyId}?activeTab=subscriptions`, '_blank');
    }
  };

  // Open Microsoft Admin Center for this client's tenant
  const handleOpenMSAdminCenter = () => {
    if (!client?.tenantId) {
      alert('No Microsoft Tenant ID configured for this client.\n\nPlease edit the client and add their Microsoft Tenant ID first.');
      return;
    }

    // Copy the URL to clipboard and show instructions
    const adminUrl = `https://admin.microsoft.com/AdminPortal/Home?tid=${client.tenantId}#/licenses`;

    const action = window.confirm(
      `MICROSOFT ADMIN CENTER FOR: ${client.companyName}\n\n` +
      `To avoid account conflicts, RIGHT-CLICK the link below and choose:\n` +
      `• Chrome: "Open link in incognito window"\n` +
      `• Edge: "Open link in InPrivate window"\n\n` +
      `Then sign in as a Global Admin from "${client.companyName}".\n\n` +
      `Click OK to copy the URL to clipboard (then paste in private window)\n` +
      `Click CANCEL to open normally (may need to switch accounts)`
    );

    if (action) {
      // Copy URL to clipboard
      navigator.clipboard.writeText(adminUrl).then(() => {
        alert(`URL copied to clipboard!\n\nOpen a new Incognito/InPrivate window and paste the URL.\n\nThen sign in as a Global Admin from "${client.companyName}".`);
      }).catch(() => {
        // Fallback if clipboard fails
        window.prompt('Copy this URL and paste in an Incognito/InPrivate window:', adminUrl);
      });
    } else {
      // Open normally
      window.open(adminUrl, '_blank');
    }
  };

  const handleVerifyBilling = async () => {
    if (!id || !client?.tenantId) return;

    // Show confirmation with instructions
    const confirmed = window.confirm(
      `BILLING VERIFICATION FOR: ${client.companyName}\n\n` +
      `A Microsoft login window will open.\n\n` +
      `IMPORTANT: You must sign in as a Global Admin account from "${client.companyName}" - NOT your own account.\n\n` +
      `After signing in, you may need to consent to allow reading subscription data.\n\n` +
      `Click OK to continue.`
    );

    if (!confirmed) return;

    setVerifyingBilling(true);
    setSyncError(null);
    setSyncSuccess(null);
    try {
      const result = await getBillingAuthUrl(id, client.tenantId);
      // Open the auth URL in a popup - user will sign in as customer Global Admin
      window.open(result.authUrl, 'billingVerification', 'width=600,height=700,scrollbars=yes');

      // Listen for message from popup when complete
      const handleMessage = (event: MessageEvent) => {
        if (event.data?.type === 'billingVerificationComplete') {
          window.removeEventListener('message', handleMessage);
          if (event.data.success) {
            setSyncSuccess(`Billing verified! Updated ${event.data.count} subscription(s) with MS billing status.`);
            // Store debug data if provided
            if (event.data.debugData) {
              setBillingDebugData(event.data.debugData);
              setShowBillingDebug(true);
            }
            refetch();
          } else {
            setSyncError(`Billing verification failed: ${event.data.error}`);
          }
          // Don't auto-close popup - let user close it manually to see debug data
        }
      };
      window.addEventListener('message', handleMessage);

      setSyncSuccess(`Sign in window opened. Sign in as a "${client.companyName}" Global Admin account.`);
    } catch (err) {
      setSyncError(err instanceof Error ? err.message : 'Failed to start billing verification');
    } finally {
      setVerifyingBilling(false);
    }
  };

  // Handle granting/updating app consent with instructions
  const handleGrantAppConsent = (isUpdate: boolean = false) => {
    if (!client?.tenantId) {
      alert('No Microsoft Tenant ID configured for this client.\n\nPlease edit the client and add their Microsoft Tenant ID first.');
      return;
    }

    // Admin consent URL - no redirect_uri, Microsoft will show their own success page
    const consentUrl = `https://login.microsoftonline.com/${client.tenantId}/v2.0/adminconsent?client_id=9c12a3a0-fdbf-47d5-a008-c9ee50c9058f&scope=https://graph.microsoft.com/.default`;

    const action = window.confirm(
      `${isUpdate ? 'UPDATE' : 'GRANT'} APP CONSENT FOR: ${client.companyName}\n\n` +
      `A Microsoft login window will open.\n\n` +
      `SIGN IN AS: A Global Admin account from "${client.companyName}"\n` +
      `(e.g., umetech@${client.companyName.toLowerCase().replace(/[^a-z0-9]/g, '')}.onmicrosoft.com)\n\n` +
      (isUpdate
        ? `This will grant the new "Password Reset" permission that was recently added.\n\n`
        : `This grants permissions for user management, license assignment, password resets, and reports.\n\n`) +
      `After signing in, click "Accept" to grant the permissions.\n\n` +
      `Click OK to open the consent window.`
    );

    if (action) {
      const popup = window.open(consentUrl, '_blank', 'width=600,height=700');

      // Show follow-up instructions
      setTimeout(() => {
        if (popup && !popup.closed) {
          alert(
            `CONSENT WINDOW OPENED\n\n` +
            `1. Sign in with a Global Admin from "${client.companyName}"\n` +
            `2. Review the permissions and click "Accept"\n` +
            `3. Close the popup when done\n` +
            `4. Click "Manage" on the GDAP Consents card to refresh status`
          );
        }
      }, 1000);
    }
  };

  // Calculate license analytics with grouped products
  const { licenseAnalytics, groupedProducts, orphanedSubs } = useMemo(() => {
    if (!client?.subscriptions) return { licenseAnalytics: null, groupedProducts: [], orphanedSubs: [] };

    // Only include ACTIVE paid subscriptions (exclude suspended, cancelled, etc.)
    const paidSubs = client.subscriptions.filter(s =>
      getSubscriptionCategory(s) === 'paid' && s.status === 'active'
    );

    // Separate orphaned subscriptions (GUID product names)
    const orphaned = paidSubs.filter(s => isGuidProductName(s.productName));
    const validSubs = paidSubs.filter(s => !isGuidProductName(s.productName));

    // MS tenant subs (source: direct) - direct purchases from Microsoft
    const msSubs = validSubs.filter(s => s.source === 'direct');
    // Pax8 subs - what we're billing/covering (also exist in MS tenant)
    const pax8Subs = validSubs.filter(s => s.source === 'pax8');

    // Group by normalized product name
    const productMap = new Map<string, GroupedProduct>();

    // First, add all MS tenant subscriptions
    msSubs.forEach(sub => {
      const normalized = normalizeProductName(sub.productName);
      const existing = productMap.get(normalized);

      if (existing) {
        existing.msTenantQty += sub.quantity;
        existing.msTenantAssigned += sub.assignedQuantity || 0;
        existing.msSubscriptions.push({
          id: sub.id,
          quantity: sub.quantity,
          assignedQuantity: sub.assignedQuantity,
          renewalDate: sub.renewalDate,
          autoRenew: sub.autoRenew,
          productName: sub.productName,
          msBillingStatus: sub.msBillingStatus,
          msNextLifecycleDate: sub.msNextLifecycleDate,
          msCreatedDate: sub.msCreatedDate,
          billingLastChecked: sub.billingLastChecked,
          msSkuId: sub.msSkuId
        });
        // Track earliest renewal
        if (sub.renewalDate && (!existing.earliestRenewal || sub.renewalDate < existing.earliestRenewal)) {
          existing.earliestRenewal = sub.renewalDate;
        }
      } else {
        // Check if this is a tenant-level SKU
        const isTenantLevel = TENANT_LEVEL_SKUS.some(sku =>
          normalized.includes(sku) || sku.includes(normalized)
        );
        // Check if this license is ignored for this client
        const isIgnored = client?.ignoredLicenses?.some(ignored =>
          normalizeProductName(ignored) === normalized
        ) || false;

        productMap.set(normalized, {
          normalizedName: normalized,
          displayName: sub.productName.replace(/\s*\[New Commerce Experience\]/gi, '').trim(),
          msTenantQty: sub.quantity,
          msTenantAssigned: sub.assignedQuantity || 0,
          pax8Qty: 0,
          pax8AutoRenewQty: 0,
          gap: 0,
          coverageStatus: 'unmatched',
          isTenantLevel,
          isIgnored,
          isPax8Only: false,  // MS subscriptions are always MS products
          earliestRenewal: sub.renewalDate,
          msSubscriptions: [{
            id: sub.id,
            quantity: sub.quantity,
            assignedQuantity: sub.assignedQuantity,
            renewalDate: sub.renewalDate,
            autoRenew: sub.autoRenew,
            productName: sub.productName,
            msBillingStatus: sub.msBillingStatus,
            msNextLifecycleDate: sub.msNextLifecycleDate,
            msCreatedDate: sub.msCreatedDate,
            billingLastChecked: sub.billingLastChecked,
            msSkuId: sub.msSkuId
          }],
          pax8Subscriptions: []
        });
      }
    });

    // Then add Pax8 subscriptions
    pax8Subs.forEach(sub => {
      const normalized = normalizeProductName(sub.productName);
      const existing = productMap.get(normalized);

      if (existing) {
        // Pax8 subs match the MS tenant license - DON'T add to msTenantQty (already counted from MS record)
        // Only track Pax8 coverage
        existing.pax8Qty += sub.quantity;
        if (sub.autoRenew) existing.pax8AutoRenewQty += sub.quantity;
        // Update assigned count if Pax8 sub has it (MS Direct might not have synced this)
        if (sub.assignedQuantity != null && sub.assignedQuantity > existing.msTenantAssigned) {
          existing.msTenantAssigned = sub.assignedQuantity;
        }
        existing.pax8Subscriptions.push({
          id: sub.id,
          quantity: sub.quantity,
          renewalDate: sub.renewalDate,
          autoRenew: sub.autoRenew,
          productName: sub.productName
        });
      } else {
        // Pax8 product with no matching MS subscription
        // Check if this is a Microsoft product or a third-party (Dropsuite, Datto, etc.)
        const isMsProduct = isMicrosoftProduct(sub.productName);
        const isTenantLevel = TENANT_LEVEL_SKUS.some(sku =>
          normalized.includes(sku) || sku.includes(normalized)
        );
        const isIgnored = client?.ignoredLicenses?.some(ignored =>
          normalizeProductName(ignored) === normalized
        ) || false;

        productMap.set(normalized, {
          normalizedName: normalized,
          displayName: sub.productName.replace(/\s*\[New Commerce Experience\]/gi, '').trim(),
          // Third-party Pax8 products (Dropsuite, etc.) are NOT MS licenses
          msTenantQty: isMsProduct ? sub.quantity : 0,
          msTenantAssigned: isMsProduct ? (sub.assignedQuantity || 0) : 0,
          pax8Qty: sub.quantity,
          pax8AutoRenewQty: sub.autoRenew ? sub.quantity : 0,
          gap: 0,
          coverageStatus: isMsProduct ? 'covered' : 'pax8-only',
          isTenantLevel: isMsProduct ? isTenantLevel : false,
          isIgnored,
          isPax8Only: !isMsProduct,  // Dropsuite, Datto, etc. are Pax8-only (not MS products)
          earliestRenewal: sub.renewalDate,
          msSubscriptions: [],
          pax8Subscriptions: [{
            id: sub.id,
            quantity: sub.quantity,
            renewalDate: sub.renewalDate,
            autoRenew: sub.autoRenew,
            productName: sub.productName
          }]
        });
      }
    });

    // Calculate coverage status and gaps
    let totalMsTenant = 0;
    let totalMsAssigned = 0;
    let totalPax8 = 0;
    let totalUnmatched = 0;
    let totalIgnored = 0;
    let totalTenantLevel = 0;
    let totalPax8Only = 0;

    productMap.forEach(group => {
      // Handle Pax8-only products (not MS licenses) - don't count in MS utilization
      if (group.isPax8Only) {
        group.coverageStatus = 'pax8-only';
        totalPax8Only += group.pax8Qty;
        return; // Skip - these aren't MS licenses
      }

      // Handle ignored licenses - mark status and don't count in totals
      if (group.isIgnored) {
        group.coverageStatus = 'ignored';
        totalIgnored += group.msTenantQty;
        return; // Skip - don't include in utilization calculation
      }

      // Handle tenant-level licenses (0 assigned but applies to tenant)
      // These are "covered" if they have matching Pax8, otherwise "tenant-level"
      if (group.isTenantLevel && group.msTenantAssigned === 0) {
        totalTenantLevel += group.msTenantQty;
        totalPax8 += group.pax8Qty;
        if (group.pax8Qty >= group.msTenantQty) {
          group.coverageStatus = 'covered';
        } else {
          group.coverageStatus = 'tenant-level';
        }
        group.gap = 0; // Tenant-level licenses don't have a gap per se
        return; // Skip - don't include in utilization (they're 100% "used" by tenant)
      }

      // Regular licenses - count in utilization
      totalMsTenant += group.msTenantQty;
      totalMsAssigned += group.msTenantAssigned;
      totalPax8 += group.pax8Qty;

      // Gap = MS tenant qty that's not covered by Pax8
      if (group.msTenantQty > 0) {
        group.gap = Math.max(0, group.msTenantQty - group.pax8Qty);
        totalUnmatched += group.gap;

        if (group.pax8Qty >= group.msTenantQty) {
          group.coverageStatus = 'covered';
        } else if (group.pax8Qty > 0) {
          group.coverageStatus = 'partial';
        } else {
          group.coverageStatus = 'unmatched';
        }
      }
    });

    // Filter out products with 0 total licenses (no MS and no Pax8)
    const nonEmptyProducts = Array.from(productMap.values()).filter(
      p => p.msTenantQty > 0 || p.pax8Qty > 0
    );

    // Sort: unmatched (top) → tenant-level → partial → covered → pax8-only → ignored (bottom)
    const sorted = nonEmptyProducts.sort((a, b) => {
      const statusOrder: Record<string, number> = {
        unmatched: 0,
        'tenant-level': 1,
        partial: 2,
        covered: 3,
        'pax8-only': 4,  // Pax8 products that aren't MS licenses (above ignored)
        ignored: 5
      };
      const statusDiff = statusOrder[a.coverageStatus] - statusOrder[b.coverageStatus];
      if (statusDiff !== 0) return statusDiff;
      return a.displayName.localeCompare(b.displayName);
    });

    // Calculate wasted Pax8 licenses (Pax8 qty > assigned)
    let wastedPax8Licenses = 0;
    let wastedPax8Amount = 0;
    for (const product of sorted) {
      if (product.pax8Qty > 0 && !product.isPax8Only && !product.isIgnored) {
        const unused = product.pax8Qty - product.msTenantAssigned;
        if (unused > 0) {
          wastedPax8Licenses += unused;
          // Estimate monthly cost from Pax8 subscriptions
          for (const pax8Sub of product.pax8Subscriptions) {
            const subData = pax8Subs.find(s => s.id === pax8Sub.id);
            if (subData?.unitPrice) {
              const subUnused = Math.min(unused, pax8Sub.quantity);
              wastedPax8Amount += subUnused * subData.unitPrice;
            }
          }
        }
      }
    }

    return {
      licenseAnalytics: {
        msTenantLicenses: totalMsTenant,
        msTenantAssigned: totalMsAssigned,
        pax8Coverage: totalPax8,
        unmatchedLicenses: totalUnmatched,
        tenantLevelLicenses: totalTenantLevel,
        ignoredLicenses: totalIgnored,
        pax8OnlyLicenses: totalPax8Only,  // Pax8 products not in MS tenant (Dropsuite, etc.)
        orphanedCount: orphaned.length,
        orphanedLicenses: orphaned.reduce((sum, s) => sum + s.quantity, 0),
        utilizationPercent: totalMsTenant > 0 ? Math.round((totalMsAssigned / totalMsTenant) * 100) : 0,
        wastedPax8Licenses,  // Unused Pax8 licenses (paying but not using)
        wastedPax8Amount     // Estimated monthly waste in $
      },
      groupedProducts: sorted,
      orphanedSubs: orphaned
    };
  }, [client?.subscriptions, client?.ignoredLicenses]);

  // Calculate license gaps (MS licenses expiring without Pax8 coverage)
  const licenseGaps = useMemo(() => {
    if (!client?.subscriptions) return [];

    const paidSubs = client.subscriptions.filter(s => getSubscriptionCategory(s) === 'paid');
    const directSubs = paidSubs.filter(s => s.source === 'direct');
    const pax8Subs = paidSubs.filter(s => s.source === 'pax8');
    const today = new Date();

    const normalizeProductName = (name: string) =>
      name.replace(/\s*\[New Commerce Experience\]/gi, '').replace(/\s*NCE/gi, '').trim().toLowerCase();

    const gaps: Array<{
      product: string;
      directExpiring: number;
      pax8Available: number;
      gap: number;
      daysUntilExpiry: number;
      severity: 'soft' | 'medium' | 'hard';
    }> = [];

    // Find expiring direct subs (within 90 days) with auto-renew OFF
    const expiringDirectSubs = directSubs.filter(s => {
      if (!s.renewalDate || s.autoRenew) return false;
      const daysUntil = differenceInDays(parseISO(s.renewalDate), today);
      return daysUntil >= 0 && daysUntil <= 90;
    });

    // Group by normalized product name
    const expiringByProduct = new Map<string, { quantity: number; daysUntil: number; originalName: string }>();
    expiringDirectSubs.forEach(s => {
      const normalized = normalizeProductName(s.productName);
      const daysUntil = differenceInDays(parseISO(s.renewalDate!), today);
      const existing = expiringByProduct.get(normalized);
      if (existing) {
        existing.quantity += s.quantity;
        if (daysUntil < existing.daysUntil) existing.daysUntil = daysUntil;
      } else {
        expiringByProduct.set(normalized, { quantity: s.quantity, daysUntil, originalName: s.productName });
      }
    });

    // Compare with Pax8 coverage (any active Pax8 sub provides coverage)
    expiringByProduct.forEach((expiring, normalizedName) => {
      const matchingPax8 = pax8Subs.filter(s =>
        normalizeProductName(s.productName) === normalizedName
      );
      const pax8Qty = matchingPax8.reduce((sum, s) => sum + s.quantity, 0);
      const gap = expiring.quantity - pax8Qty;

      if (gap > 0) {
        let severity: 'soft' | 'medium' | 'hard' = 'soft';
        if (expiring.daysUntil <= 30) severity = 'hard';
        else if (expiring.daysUntil <= 60) severity = 'medium';

        gaps.push({
          product: expiring.originalName,
          directExpiring: expiring.quantity,
          pax8Available: pax8Qty,
          gap,
          daysUntilExpiry: expiring.daysUntil,
          severity
        });
      }
    });

    // Sort by severity (hard first)
    gaps.sort((a, b) => {
      const severityOrder = { hard: 0, medium: 1, soft: 2 };
      return severityOrder[a.severity] - severityOrder[b.severity];
    });

    return gaps;
  }, [client?.subscriptions]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error || !client) {
    return (
      <div className="text-center py-12">
        <h3 className="text-lg font-medium text-foreground">
          Client not found
        </h3>
        <p className="text-muted-foreground mt-2">{error}</p>
        <Link to="/clients" className="btn-primary mt-4 inline-flex">
          Back to Clients
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/clients')}
            className="p-2 rounded-md hover:bg-accent"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-foreground">
              {client.companyName}
            </h1>
            <div className="flex items-center space-x-2 mt-1">
              <StatusBadge status={client.status} type="client" />
              <StatusBadge status={client.paymentStatus} type="payment" />
              {licenseAnalytics && licenseAnalytics.unmatchedLicenses > 0 && (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200">
                  <AlertTriangle className="h-3 w-3 mr-1" />
                  {licenseAnalytics.unmatchedLicenses} unmatched
                </span>
              )}
              {licenseAnalytics && licenseAnalytics.orphanedCount > 0 && (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200">
                  <AlertTriangle className="h-3 w-3 mr-1" />
                  {licenseAnalytics.orphanedCount} orphaned
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="mt-4 sm:mt-0 flex flex-wrap items-center gap-2">
          <div className="flex flex-col gap-0.5">
            <div className="flex items-center gap-1">
              <button
                onClick={handleSyncPax8}
                disabled={syncingPax8 || syncingMS}
                className="btn-secondary"
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${syncingPax8 ? 'animate-spin' : ''}`} />
                {syncingPax8 ? 'Syncing...' : 'Refresh Pax8'}
              </button>
              <HelpTooltip text="Syncs subscription data from Pax8 for this client only. Updates pricing, quantities, and renewal dates." size="sm" />
            </div>
            <button
              onClick={handleOpenPax8Portal}
              className="text-xs text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 flex items-center justify-center gap-1"
            >
              <ExternalLink className="h-3 w-3" />
              Open Pax8 Portal
            </button>
          </div>
          <div className="flex flex-col gap-0.5">
            <div className="flex items-center gap-1">
              <button
                onClick={handleSyncMS}
                disabled={syncingPax8 || syncingMS}
                className="btn-secondary"
              >
                <Cloud className={`h-4 w-4 mr-2 ${syncingMS ? 'animate-spin' : ''}`} />
                {syncingMS ? 'Syncing...' : 'Refresh MS'}
              </button>
              <HelpTooltip text="Syncs license data directly from the customer's Microsoft 365 tenant via GDAP. Shows how many licenses are actually assigned to users." size="sm" />
            </div>
            <button
              onClick={handleOpenMSAdminCenter}
              className="text-xs text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 flex items-center justify-center gap-1"
            >
              <ExternalLink className="h-3 w-3" />
              Open MS Admin Center
            </button>
          </div>
          {client.tenantId && (
            <div className="flex items-center gap-1">
              <button
                onClick={handleVerifyBilling}
                disabled={verifyingBilling || !client.tenantId}
                className="btn-secondary"
              >
                <DollarSign className={`h-4 w-4 mr-2 ${verifyingBilling ? 'animate-pulse' : ''}`} />
                {verifyingBilling ? 'Opening...' : 'Verify Billing'}
              </button>
              <HelpTooltip text="Opens a login window. Sign in as the customer's Global Admin to verify their MS billing status, renewal dates, and auto-renew settings." size="sm" />
            </div>
          )}
          {client.tenantId && (
            <div className="flex items-center gap-1">
              <button
                onClick={() => {
                  fetchConsentStatus();
                  setShowConsentWizard(true);
                }}
                disabled={consentLoading}
                className={`btn-secondary ${
                  consentStatus?.allConsentsGranted && consentStatus?.passwordResetPermission?.granted && consentStatus?.groupMemberPermission?.granted
                    ? 'text-green-600 border-green-300 dark:text-green-400 dark:border-green-700'
                    : !consentStatus
                    ? ''
                    : 'text-orange-600 border-orange-300 dark:text-orange-400 dark:border-orange-700'
                }`}
              >
                <ShieldCheck className={`h-4 w-4 mr-2 ${consentLoading ? 'animate-pulse' : ''}`} />
                {consentLoading ? 'Checking...' : consentStatus?.allConsentsGranted && consentStatus?.passwordResetPermission?.granted && consentStatus?.groupMemberPermission?.granted ? 'Consents OK' : 'Grant Consents'}
              </button>
              <HelpTooltip text="Check and grant GDAP consents for this client. Required for accessing users, groups, and running reports." size="sm" />
            </div>
          )}
          {client.tenantId && (
            <Link
              to={`/message-trace?clientId=${client.id}`}
              className="btn-secondary inline-flex items-center"
            >
              <Mail className="h-4 w-4 mr-2" />
              Message Trace
            </Link>
          )}
          {client.tenantId && (
            <button
              onClick={() => setShowEmailAuth(!showEmailAuth)}
              className={`btn-secondary inline-flex items-center ${showEmailAuth ? 'text-primary-600 border-primary-300 dark:text-primary-400 dark:border-primary-700' : ''}`}
            >
              <ShieldCheck className="h-4 w-4 mr-2" />
              Email Auth
            </button>
          )}
          {client.tenantId && (
            <Link
              to={`/compliance/${client.id}`}
              className="btn-secondary inline-flex items-center"
            >
              <ShieldCheck className="h-4 w-4 mr-2" />
              HIPAA Compliance
            </Link>
          )}
          <button
            onClick={() => setShowEditForm(true)}
            className="btn-secondary"
          >
            <Pencil className="h-4 w-4 mr-2" />
            Edit
          </button>
        </div>
      </div>

      {/* Sync Status Messages */}
      {syncError && (
        <div className="bg-red-50 dark:bg-red-900/50 border border-red-200 dark:border-red-800 rounded-md p-3">
          <p className="text-sm text-red-600 dark:text-red-400">{syncError}</p>
        </div>
      )}
      {syncSuccess && (
        <div className="bg-green-50 dark:bg-green-900/50 border border-green-200 dark:border-green-800 rounded-md p-3">
          <p className="text-sm text-green-600 dark:text-green-400">{syncSuccess}</p>
        </div>
      )}

      {/* Email Auth Results */}
      {showEmailAuth && client.tenantId && (
        <EmailAuthResults
          clientId={client.id}
          onClose={() => setShowEmailAuth(false)}
        />
      )}

      {/* Billing Debug Data */}
      {billingDebugData && (
        <div className="bg-gray-900 border border-gray-700 rounded-md overflow-hidden">
          <button
            onClick={() => setShowBillingDebug(!showBillingDebug)}
            className="w-full flex items-center justify-between p-3 text-left hover:bg-gray-800"
          >
            <span className="text-sm font-medium text-blue-400">
              DEBUG: Raw Microsoft API Responses
            </span>
            <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${showBillingDebug ? 'rotate-180' : ''}`} />
          </button>
          {showBillingDebug && (
            <div className="p-3 border-t border-gray-700">
              <pre className="text-xs text-gray-300 overflow-auto max-h-96 whitespace-pre-wrap">
                {JSON.stringify(billingDebugData, null, 2)}
              </pre>
              <button
                onClick={() => setBillingDebugData(null)}
                className="mt-2 text-xs text-red-400 hover:text-red-300"
              >
                Clear debug data
              </button>
            </div>
          )}
        </div>
      )}

      {/* MS Sync Status Header - only show if client has a tenant */}
      {client.tenantId && (
        <SyncStatusHeader
          syncData={syncData}
          syncing={msSyncing || smartSyncing}
          staleness={staleness}
          onSync={handleSmartSync}
        />
      )}

      {/* Tabs */}
      <div className="border-b border-border">
        <nav className="-mb-px flex space-x-8">
          <div className="flex items-center">
            <button
              onClick={() => setActiveTab('subscriptions')}
              className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'subscriptions'
                  ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                  : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
              }`}
            >
              <CreditCard className="h-5 w-5 inline mr-2" />
              Subscriptions
            </button>
            <HelpTooltip text="View all subscriptions for this client. Shows Pax8 and MS Direct licenses with coverage status." size="sm" />
          </div>
          <div className="flex items-center">
            <button
              onClick={() => setActiveTab('users')}
              className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'users'
                  ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                  : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
              }`}
            >
              <Users className="h-5 w-5 inline mr-2" />
              Users
              {userCount > 0 && (
                <span className="ml-2 bg-muted text-muted-foreground px-2 py-0.5 rounded-full text-xs">
                  {userCount}
                </span>
              )}
            </button>
            <HelpTooltip text="View and manage tenant users. Click a user to assign/remove licenses, reset password, add to groups, or offboard." size="sm" />
          </div>
          {client.workspaceType !== 'google' && (
          <div className="flex items-center">
            <button
              onClick={() => setActiveTab('groups')}
              className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'groups'
                  ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                  : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
              }`}
            >
              <UsersRound className="h-5 w-5 inline mr-2" />
              Groups
              {(syncData?.groups?.count ?? 0) > 0 && (
                <span className="ml-2 bg-muted text-muted-foreground px-2 py-0.5 rounded-full text-xs">
                  {syncData?.groups?.count ?? 0}
                </span>
              )}
            </button>
            <HelpTooltip text="View all groups in the tenant: Security, Microsoft 365, and Distribution groups." size="sm" />
          </div>
          )}
          <div className="flex items-center">
            <button
              onClick={() => setActiveTab('security')}
              className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'security'
                  ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                  : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
              }`}
            >
              <ShieldCheck className="h-5 w-5 inline mr-2" />
              Security
              {securitySnapshot && (
                <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${
                  securitySnapshot.mfa.coverage >= 80
                    ? 'bg-green-100 dark:bg-green-900 text-green-600 dark:text-green-300'
                    : securitySnapshot.mfa.coverage >= 50
                    ? 'bg-yellow-100 dark:bg-yellow-900 text-yellow-600 dark:text-yellow-300'
                    : 'bg-red-100 dark:bg-red-900 text-red-600 dark:text-red-300'
                }`}>
                  {securitySnapshot.mfa.coverage}% MFA
                </span>
              )}
            </button>
            <HelpTooltip text="View security metrics: MFA coverage, Secure Score, admin accounts, and conditional access policies." size="sm" />
          </div>
          {client.workspaceType !== 'google' && (
          <div className="flex items-center">
            <button
              onClick={() => setActiveTab('mailboxes')}
              className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'mailboxes'
                  ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                  : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
              }`}
            >
              <Mail className="h-5 w-5 inline mr-2" />
              Mailboxes
              {((syncData?.userMailboxes?.data?.length || 0) + (syncData?.sharedMailboxes?.data?.length || 0)) > 0 && (
                <span className="ml-1.5 inline-flex items-center justify-center px-2 py-0.5 rounded-full text-xs font-medium bg-primary-100 dark:bg-primary-900/40 text-primary-700 dark:text-primary-300">
                  {(syncData?.userMailboxes?.data?.length || 0) + (syncData?.sharedMailboxes?.data?.length || 0)}
                </span>
              )}
            </button>
            <HelpTooltip text="View all mailboxes and manage shared mailboxes for this client." size="sm" />
          </div>
          )}
          {client.workspaceType !== 'google' && (
          <div className="flex items-center">
            <button
              onClick={() => setActiveTab('sharepoint')}
              className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'sharepoint'
                  ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                  : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
              }`}
            >
              <Globe className="h-5 w-5 inline mr-2" />
              SharePoint
            </button>
            <HelpTooltip text="View SharePoint sites and manage site permissions (Owners/Members/Visitors)." size="sm" />
          </div>
          )}
          {client.workspaceType !== 'google' && client.tenantId && (
            <div className="flex items-center">
              <button
                onClick={() => setActiveTab('soc')}
                className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === 'soc'
                    ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                    : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
                }`}
              >
                <Shield className="h-5 w-5 inline mr-2" />
                SOC
              </button>
              <HelpTooltip text="Security Operations Center — forensic data collection, IOC detection, and security alerts for this client." size="sm" />
            </div>
          )}
          {client.tenantId && (
            <div className="flex items-center gap-1">
              <button
                onClick={() => setActiveTab('emailhealth')}
                className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === 'emailhealth'
                    ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                    : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
                }`}
              >
                <Mail className="h-5 w-5 inline mr-2" />
                Email Health
              </button>
              <HelpTooltip text="SpamHero connector health check — verifies MX routing, inbound connector, Enhanced Filtering, and IP configuration." size="sm" />
            </div>
          )}
          {client.tenantId && (
            <div className="flex items-center gap-1">
              <button
                onClick={() => setActiveTab('dmarc')}
                className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === 'dmarc'
                    ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                    : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
                }`}
              >
                <Shield className="h-5 w-5 inline mr-2" />
                DMARC
              </button>
              <HelpTooltip text="DMARC aggregate report ingestion — shows source IPs claiming to send as this domain, alignment results, and policy status." size="sm" />
            </div>
          )}
          {(client.workspaceType === 'google' || client.workspaceType === 'hybrid') && (
            <div className="flex items-center">
              <button
                onClick={() => setActiveTab('google-oauth')}
                className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === 'google-oauth'
                    ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                    : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
                }`}
              >
                <ShieldCheck className="h-5 w-5 inline mr-2" />
                OAuth Apps
                {(syncData?.googleOAuthApps?.count ?? 0) > 0 && (
                  <span className="ml-2 bg-muted text-muted-foreground px-2 py-0.5 rounded-full text-xs">
                    {syncData?.googleOAuthApps?.count ?? 0}
                  </span>
                )}
              </button>
              <HelpTooltip text="Third-party OAuth apps authorized by users in the Google Workspace domain." size="sm" />
            </div>
          )}
          {(client.workspaceType === 'google' || client.workspaceType === 'hybrid') && (
            <div className="flex items-center">
              <button
                onClick={() => setActiveTab('google-devices')}
                className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === 'google-devices'
                    ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                    : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
                }`}
              >
                <Globe className="h-5 w-5 inline mr-2" />
                Devices
                {(syncData?.googleMobileDevices?.count ?? 0) > 0 && (
                  <span className="ml-2 bg-muted text-muted-foreground px-2 py-0.5 rounded-full text-xs">
                    {syncData?.googleMobileDevices?.count ?? 0}
                  </span>
                )}
              </button>
              <HelpTooltip text="Mobile devices registered to the Google Workspace domain. Flags outdated OS versions." size="sm" />
            </div>
          )}
          {(client.workspaceType === 'google' || client.workspaceType === 'hybrid') && (
            <div className="flex items-center">
              <button
                onClick={() => setActiveTab('google-audit')}
                className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === 'google-audit'
                    ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                    : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
                }`}
              >
                <FileText className="h-5 w-5 inline mr-2" />
                Admin Audit
                {(syncData?.googleAdminAudit?.count ?? 0) > 0 && (
                  <span className="ml-2 bg-muted text-muted-foreground px-2 py-0.5 rounded-full text-xs">
                    {syncData?.googleAdminAudit?.count ?? 0}
                  </span>
                )}
              </button>
              <HelpTooltip text="Google Workspace admin activity log. Highlights sensitive actions like password and role changes." size="sm" />
            </div>
          )}
        </nav>
      </div>

      {/* Client Info Grid */}
      <div className="relative flex flex-col lg:flex-row gap-6">
        {/* Main Info */}
        <div className={`min-w-0 flex-1 transition-all duration-300 ease-in-out ${sidebarCollapsed ? '' : 'lg:mr-[340px]'}`}>
          {/* Details Card */}
          <div className="card p-6">
            <div className="flex items-center gap-2 mb-4">
              <h3 className="text-lg font-medium text-foreground">
                Client Details
              </h3>
              <HelpTooltip text="Basic information about this client including Pax8 Company ID (for billing) and Tenant ID (for GDAP access to Microsoft 365)." size="sm" />
            </div>
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <dt className="text-sm text-muted-foreground">
                  Pax8 Company ID
                </dt>
                <dd className="mt-1 text-sm text-foreground font-mono">
                  {client.pax8CompanyId || 'Not set'}
                </dd>
              </div>
              <div>
                <dt className="text-sm text-muted-foreground">
                  Tenant ID (M365)
                </dt>
                <dd className="mt-1 text-sm text-foreground font-mono">
                  {client.tenantId || 'Not set'}
                </dd>
              </div>
              <div>
                <dt className="text-sm text-muted-foreground">
                  Created
                </dt>
                <dd className="mt-1 text-sm text-foreground">
                  {format(parseISO(client.createdDate), 'MMM d, yyyy h:mm a')}
                </dd>
              </div>
              <div>
                <dt className="text-sm text-muted-foreground">
                  Last Modified
                </dt>
                <dd className="mt-1 text-sm text-foreground">
                  {format(parseISO(client.modifiedDate), 'MMM d, yyyy h:mm a')}
                  {client.modifiedBy && (
                    <span className="text-muted-foreground">
                      {' '}by {client.modifiedBy}
                    </span>
                  )}
                </dd>
              </div>
              {client.paymentFlaggedDate && (
                <div className="sm:col-span-2">
                  <dt className="text-sm text-muted-foreground">
                    Payment Flagged Date
                  </dt>
                  <dd className="mt-1 text-sm text-red-600 dark:text-red-400">
                    {format(parseISO(client.paymentFlaggedDate), 'MMM d, yyyy')}
                  </dd>
                </div>
              )}
            </dl>

            {client.notes && (
              <div className="mt-6 pt-4 border-t border-border">
                <h4 className="text-sm font-medium text-foreground mb-2">
                  Notes
                </h4>
                <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                  {client.notes}
                </p>
              </div>
            )}
          </div>

          {/* Subscriptions - Grouped View */}
          {activeTab === 'subscriptions' && (
          <div className="card p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <h3 className="text-lg font-medium text-foreground">
                  License Coverage
                </h3>
                <HelpTooltip text="Shows how Microsoft licenses are covered by Pax8 subscriptions. Green = Pax8 covered, Orange = purchased directly from MS (no Pax8 billing), Yellow = partially covered." size="sm" />
                {client.modifiedDate && (
                  <span className="flex items-center text-xs px-2 py-1 rounded-full bg-muted text-muted-foreground">
                    <Clock className="h-3 w-3 mr-1" />
                    Data as of {format(parseISO(client.modifiedDate), 'MMM d, h:mm a')}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={handleRefreshSubscriptions}
                  disabled={subsRefreshing || syncingPax8 || syncingMS}
                  className="text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400 flex items-center disabled:opacity-50"
                  title="Refresh Pax8 and MS subscription data"
                >
                  <RefreshCw className={`h-4 w-4 mr-1 ${subsRefreshing ? 'animate-spin' : ''}`} />
                  {subsRefreshing ? 'Refreshing...' : 'Refresh'}
                </button>
                <button
                  onClick={() => setShowLicenseReport(true)}
                  className="text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400 flex items-center"
                  title="View license assignment report"
                >
                  <FileText className="h-4 w-4 mr-1" />
                  License Report
                </button>
                <Link
                  to={`/subscriptions?clientId=${client.id}`}
                  className="text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400"
                >
                  View All Subscriptions
                </Link>
              </div>
            </div>

            {/* Wasted Pax8 Licenses Banner */}
            {licenseAnalytics && licenseAnalytics.wastedPax8Licenses > 0 && (
              <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 rounded-lg">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400 mr-2" />
                    <span className="font-medium text-red-700 dark:text-red-300">
                      {licenseAnalytics.wastedPax8Licenses} WASTED Pax8 Licenses
                    </span>
                  </div>
                  {licenseAnalytics.wastedPax8Amount > 0 && (
                    <span className="text-red-600 dark:text-red-400 font-medium">
                      ~${licenseAnalytics.wastedPax8Amount.toFixed(2)}/month
                    </span>
                  )}
                </div>
                <p className="text-sm text-red-600 dark:text-red-400 mt-1">
                  You're paying for Pax8 licenses that aren't being used. Review the products below marked with unused licenses.
                </p>
              </div>
            )}

            {/* Legend */}
            <div className="flex flex-wrap items-center gap-3 mb-4 p-2 bg-muted rounded-lg text-xs">
              <span className="flex items-center">
                <span className="w-3 h-3 rounded-full bg-orange-500 mr-1"></span>
                Unmatched
              </span>
              <span className="flex items-center">
                <span className="w-3 h-3 rounded-full bg-cyan-500 mr-1"></span>
                Tenant-Level
              </span>
              <span className="flex items-center">
                <span className="w-3 h-3 rounded-full bg-yellow-500 mr-1"></span>
                Partial
              </span>
              <span className="flex items-center">
                <span className="w-3 h-3 rounded-full bg-green-500 mr-1"></span>
                Pax8 Covered
              </span>
              <span className="flex items-center">
                <span className="w-3 h-3 rounded-full bg-purple-500 mr-1"></span>
                Pax8-Only
              </span>
              <span className="flex items-center">
                <span className="w-3 h-3 rounded-full bg-blue-500 mr-1"></span>
                Ignored
              </span>
            </div>

            {groupedProducts.length > 0 ? (
              <div className="space-y-2">
                {/* Filter out pax8-only products - they're shown in their own section below */}
                {groupedProducts.filter(g => g.coverageStatus !== 'pax8-only').map((group) => {
                  const isExpanded = expandedGroups.has(group.normalizedName);
                  return (
                    <div key={group.normalizedName} className="border border-border rounded-lg overflow-hidden">
                      {/* Group Header */}
                      <button
                        onClick={() => toggleGroup(group.normalizedName)}
                        className={`w-full flex items-center justify-between p-3 text-left hover:bg-accent ${
                          group.coverageStatus === 'unmatched' ? 'bg-orange-50 dark:bg-orange-900/20' :
                          group.coverageStatus === 'tenant-level' ? 'bg-cyan-50 dark:bg-cyan-900/20' :
                          group.coverageStatus === 'partial' ? 'bg-yellow-50 dark:bg-yellow-900/20' :
                          group.coverageStatus === 'ignored' ? 'bg-blue-50 dark:bg-blue-900/20' :
                          'bg-card'
                        }`}
                      >
                        <div className="flex items-center space-x-3 min-w-0 flex-1">
                          {/* Coverage indicator */}
                          <span className={`w-3 h-3 rounded-full flex-shrink-0 ${
                            group.coverageStatus === 'covered' ? 'bg-green-500' :
                            group.coverageStatus === 'partial' ? 'bg-yellow-500' :
                            group.coverageStatus === 'tenant-level' ? 'bg-cyan-500' :
                            group.coverageStatus === 'ignored' ? 'bg-blue-500' :
                            'bg-orange-500'
                          }`}></span>
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium text-foreground truncate">
                              {group.displayName}
                              {group.isIgnored && <span className="ml-2 text-xs text-blue-600 dark:text-blue-400">(Ignored)</span>}
                              {group.isTenantLevel && !group.isIgnored && <span className="ml-2 text-xs text-cyan-600 dark:text-cyan-400">(Tenant-Level)</span>}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              MS: {group.msTenantQty} licenses
                              {group.isTenantLevel ? (
                                <span className="text-cyan-600 dark:text-cyan-400"> (applied to tenant)</span>
                              ) : (group.msTenantQty - group.msTenantAssigned) > 0 && (
                                <span className="text-blue-600 dark:text-blue-400"> ({group.msTenantQty - group.msTenantAssigned} available)</span>
                              )}
                              {group.pax8Qty > 0 && ` • Pax8: ${group.pax8Qty}`}
                              {group.gap > 0 && !group.isIgnored && (
                                <span className="text-orange-600 dark:text-orange-400"> • {group.gap} unmatched</span>
                              )}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          {group.earliestRenewal && (
                            <span className="text-xs text-muted-foreground">
                              <Calendar className="h-3 w-3 inline mr-1" />
                              {format(parseISO(group.earliestRenewal), 'MMM d')}
                            </span>
                          )}
                          <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                        </div>
                      </button>

                      {/* Expanded Details */}
                      {isExpanded && (
                        <div className="border-t border-border bg-muted p-3 space-y-2">
                          {/* MS Tenant Subscriptions */}
                          {group.msSubscriptions.length > 0 && (
                            <div>
                              <p className="text-xs font-medium text-muted-foreground mb-1">MS Tenant:</p>
                              {group.msSubscriptions.map(sub => {
                                const availableLicenses = sub.quantity - (sub.assignedQuantity || 0);
                                return (
                                  <div
                                    key={sub.id}
                                    className="flex items-center justify-between py-1 px-2 text-xs hover:bg-accent rounded group"
                                  >
                                    <Link
                                      to={`/subscriptions/${sub.id}`}
                                      className="flex items-center space-x-2 flex-1 min-w-0"
                                    >
                                      <span className="text-foreground">
                                        {sub.quantity} licenses
                                        {sub.assignedQuantity !== null && ` (${sub.assignedQuantity} assigned)`}
                                      </span>
                                      {sub.msBillingStatus && (
                                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                                          (sub.msBillingStatus === 'active' && sub.autoRenew === true) ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300' :
                                          (sub.msBillingStatus === 'active' && sub.autoRenew === false) ? 'bg-orange-100 text-orange-700 dark:bg-orange-900 dark:text-orange-300' :
                                          (sub.msBillingStatus === 'active') ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300' :
                                          sub.msBillingStatus === 'warning' ? 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300' :
                                          sub.msBillingStatus === 'suspended' ? 'bg-gray-100 text-gray-700 dark:bg-gray-600 dark:text-gray-300' :
                                          'bg-gray-100 text-gray-700 dark:bg-gray-600 dark:text-gray-300'
                                        }`}>
                                          {(sub.msBillingStatus === 'active' && sub.autoRenew === true) ? 'Renewing' :
                                           (sub.msBillingStatus === 'active' && sub.autoRenew === false) ? 'Expiring!' :
                                           (sub.msBillingStatus === 'active') ? 'Active' :
                                           sub.msBillingStatus === 'warning' ? 'Grace Period' :
                                           sub.msBillingStatus === 'suspended' ? 'Suspended' :
                                           sub.msBillingStatus}
                                        </span>
                                      )}
                                    </Link>
                                    <div className="flex items-center space-x-2">
                                      {/* Debug: show if msSkuId exists */}
                                      {!sub.msSkuId && availableLicenses > 0 && (
                                        <span className="text-[9px] text-red-500" title="msSkuId is missing - run Refresh MS">
                                          [No SKU ID]
                                        </span>
                                      )}
                                      {sub.msSkuId && availableLicenses > 0 && (
                                        <button
                                          onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            setBulkAssignModal({
                                              isOpen: true,
                                              skuId: sub.msSkuId!,
                                              productName: sub.productName.replace(/\s*\[New Commerce Experience\]/gi, '').trim(),
                                              available: availableLicenses
                                            });
                                          }}
                                          className="px-2 py-0.5 text-[10px] font-medium text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/30 rounded hover:bg-primary-100 dark:hover:bg-primary-900/50 flex items-center"
                                          title={`Assign ${availableLicenses} available license${availableLicenses !== 1 ? 's' : ''} to users`}
                                        >
                                          <UserPlus className="h-3 w-3 mr-1" />
                                          Assign
                                        </button>
                                      )}
                                      <span className="text-muted-foreground text-right">
                                        {sub.msNextLifecycleDate ? (
                                          <span className={`font-medium ${(sub.msBillingStatus === 'warning' || sub.autoRenew === false) ? 'text-orange-600 dark:text-orange-400' : 'text-foreground'}`}>
                                            {(sub.msBillingStatus === 'warning' || sub.autoRenew === false) ? 'Expires:' : 'Renewal:'} {format(parseISO(sub.msNextLifecycleDate), 'MMM d, yyyy')}
                                          </span>
                                        ) : sub.renewalDate ? (
                                          format(parseISO(sub.renewalDate), 'MMM d, yyyy')
                                        ) : 'No date'}
                                        {sub.msCreatedDate && (
                                          <span className="block text-[10px] text-muted-foreground">
                                            (purchased {format(parseISO(sub.msCreatedDate), 'M/d/yy')})
                                          </span>
                                        )}
                                      </span>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                          {/* Pax8 Subscriptions */}
                          {group.pax8Subscriptions.length > 0 && (
                            <div>
                              <p className="text-xs font-medium text-green-600 dark:text-green-400 mb-1">Pax8 Coverage:</p>
                              {group.pax8Subscriptions.map(sub => (
                                <Link
                                  key={sub.id}
                                  to={`/subscriptions/${sub.id}`}
                                  className="flex items-center justify-between py-1 px-2 text-xs hover:bg-accent rounded"
                                >
                                  <span className="text-foreground">
                                    {sub.quantity} licenses
                                  </span>
                                  <span className="text-muted-foreground">
                                    {sub.renewalDate ? format(parseISO(sub.renewalDate), 'MMM d, yyyy') : 'No date'}
                                    {sub.autoRenew && ' • Auto-renew'}
                                  </span>
                                </Link>
                              ))}
                            </div>
                          )}
                          {/* Coverage gap notice */}
                          {group.gap > 0 && group.pax8Subscriptions.length === 0 && !group.isIgnored && (
                            <p className="text-xs text-orange-600 dark:text-orange-400 italic">
                              No Pax8 coverage found - check if purchased directly from Microsoft
                            </p>
                          )}

                          {/* Ignore/Unignore button - only show for products with NO Pax8 coverage, or if already ignored */}
                          {(group.pax8Qty === 0 || group.isIgnored) && (
                            <div className="mt-2 pt-2 border-t border-border">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleIgnoredLicense(group.normalizedName);
                                }}
                                className={`text-xs px-2 py-1 rounded ${
                                  group.isIgnored
                                    ? 'bg-muted text-foreground hover:bg-accent'
                                    : 'bg-blue-100 text-blue-700 hover:bg-blue-200 dark:bg-blue-900 dark:text-blue-300 dark:hover:bg-blue-800'
                                }`}
                              >
                                {group.isIgnored ? 'Unignore (Include in Coverage)' : 'Ignore (Purchased Direct from Microsoft)'}
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}

                {/* Orphaned Subscriptions Section */}
                {orphanedSubs.length > 0 && (
                  <div className="mt-4 border-t border-border pt-4">
                    <button
                      onClick={() => setShowOrphaned(!showOrphaned)}
                      className="flex items-center justify-between w-full text-left text-sm text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300"
                    >
                      <span className="flex items-center">
                        <AlertTriangle className="h-4 w-4 mr-2" />
                        Orphaned/Unknown Records ({orphanedSubs.length} subscriptions, {orphanedSubs.reduce((sum, s) => sum + s.quantity, 0)} licenses)
                      </span>
                      <ChevronDown className={`h-4 w-4 transition-transform ${showOrphaned ? 'rotate-180' : ''}`} />
                    </button>

                    {showOrphaned && (
                      <div className="mt-3 space-y-2">
                        <p className="text-xs text-muted-foreground mb-2">
                          These records have GUID product names - they may be stale data or failed syncs.
                        </p>
                        {orphanedSubs.map((sub) => (
                          <Link
                            key={sub.id}
                            to={`/subscriptions/${sub.id}`}
                            className="flex items-center justify-between p-2 rounded-lg bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/30 border border-red-200 dark:border-red-800"
                          >
                            <div className="min-w-0 flex-1">
                              <p className="text-xs font-mono text-red-700 dark:text-red-300 truncate">
                                {sub.productName}
                              </p>
                              <p className="text-xs text-red-600 dark:text-red-400">
                                {sub.quantity} licenses • Source: {sub.source}
                              </p>
                            </div>
                            <StatusBadge status={sub.status} type="subscription" />
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Pax8-Only Products Section (Dropsuite, Datto, etc.) - Collapsible */}
                {(() => {
                  const pax8OnlyProducts = groupedProducts.filter(g => g.coverageStatus === 'pax8-only');
                  if (pax8OnlyProducts.length === 0) return null;

                  const pax8OnlyCount = pax8OnlyProducts.length;
                  const pax8OnlyLicenses = pax8OnlyProducts.reduce((sum, g) => sum + g.pax8Qty, 0);

                  return (
                    <div className="mt-4 border-t border-border pt-4">
                      <button
                        onClick={() => setShowPax8Only(!showPax8Only)}
                        className="flex items-center justify-between w-full text-left text-sm text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300"
                      >
                        <span className="flex items-center">
                          <ShoppingCart className="h-4 w-4 mr-2" />
                          Pax8-Only Products ({pax8OnlyCount} products, {pax8OnlyLicenses.toLocaleString()} licenses)
                        </span>
                        <ChevronDown className={`h-4 w-4 transition-transform ${showPax8Only ? 'rotate-180' : ''}`} />
                      </button>

                      {showPax8Only && (
                        <div className="mt-3 space-y-2">
                          <p className="text-xs text-muted-foreground mb-2">
                            These are Pax8 products that are NOT Microsoft licenses (e.g., Dropsuite, Datto backup). They don't count toward MS license utilization.
                          </p>
                          {pax8OnlyProducts.map((group) => (
                            <div
                              key={group.normalizedName}
                              className="flex items-center justify-between p-3 rounded-lg bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800"
                            >
                              <div className="flex items-center space-x-3">
                                <span className="w-3 h-3 rounded-full bg-purple-500 flex-shrink-0"></span>
                                <div>
                                  <p className="text-sm font-medium text-purple-800 dark:text-purple-200">
                                    {group.displayName}
                                  </p>
                                  <p className="text-xs text-purple-600 dark:text-purple-400">
                                    Pax8: {group.pax8Qty} licenses
                                    {group.earliestRenewal && (
                                      <span className="ml-2">
                                        <Calendar className="h-3 w-3 inline mr-1" />
                                        {format(parseISO(group.earliestRenewal), 'MMM d, yyyy')}
                                      </span>
                                    )}
                                  </p>
                                </div>
                              </div>
                              {group.pax8Subscriptions.length > 0 && (
                                <Link
                                  to={`/subscriptions/${group.pax8Subscriptions[0].id}`}
                                  className="text-xs text-purple-600 hover:text-purple-800 dark:text-purple-400 dark:hover:text-purple-200"
                                >
                                  View →
                                </Link>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })()}

                {/* Trial & Free Subscriptions - Collapsible */}
                {client.subscriptions && (() => {
                  const trialFreeSubs = client.subscriptions.filter(
                    (sub) => getSubscriptionCategory(sub) !== 'paid'
                  );
                  const trialFreeCount = trialFreeSubs.length;
                  const trialFreeLicenses = trialFreeSubs.reduce((sum, s) => sum + s.quantity, 0);

                  if (trialFreeCount === 0) return null;

                  return (
                    <div className="mt-4 border-t border-border pt-4">
                      <button
                        onClick={() => setShowTrialFree(!showTrialFree)}
                        className="flex items-center justify-between w-full text-left text-sm text-muted-foreground hover:text-foreground"
                      >
                        <span>
                          Trial & Free Licenses ({trialFreeCount} subscriptions, {trialFreeLicenses.toLocaleString()} licenses)
                        </span>
                        <ChevronDown
                          className={`h-4 w-4 transition-transform ${showTrialFree ? 'rotate-180' : ''}`}
                        />
                      </button>

                      {showTrialFree && (
                        <div className="mt-3 space-y-2">
                          {trialFreeSubs.map((sub) => {
                            const category = getSubscriptionCategory(sub);
                            return (
                              <Link
                                key={sub.id}
                                to={`/subscriptions/${sub.id}`}
                                className="flex items-center justify-between p-3 rounded-lg bg-muted/50 hover:bg-accent opacity-60 hover:opacity-100 transition-opacity"
                              >
                                <div className="flex items-center space-x-3">
                                  <CreditCard className="h-5 w-5 text-muted-foreground" />
                                  <div>
                                    <div className="flex items-center space-x-2">
                                      <p className="text-sm text-muted-foreground">
                                        {sub.productName}
                                      </p>
                                      <StatusBadge status={category} />
                                    </div>
                                    <p className="text-xs text-muted-foreground">
                                      {sub.quantity.toLocaleString()} licenses
                                    </p>
                                  </div>
                                </div>
                              </Link>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground text-center py-4">
                No subscriptions found
              </p>
            )}
          </div>
          )}

          {/* Users Tab */}
          {activeTab === 'users' && (
            <div className="card p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-foreground">
                  Tenant Users
                </h3>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setShowLicenseReport(true)}
                    className="text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400 flex items-center"
                    title="View license assignment report"
                  >
                    <FileText className="h-4 w-4 mr-1" />
                    License Report
                  </button>
                  <button
                    onClick={() => refetchUsers()}
                    className="text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400 flex items-center"
                  >
                    <RefreshCw className="h-4 w-4 mr-1" />
                    Refresh
                  </button>
                </div>
              </div>
              {!client.tenantId ? (
                <div className="text-center py-8">
                  <Users className="h-12 w-12 mx-auto text-muted-foreground" />
                  <p className="mt-2 text-muted-foreground">No Tenant ID configured</p>
                  <p className="text-sm text-muted-foreground">
                    Add a Microsoft Tenant ID to view users
                  </p>
                </div>
              ) : (
                <UserList
                  users={users}
                  loading={usersLoading}
                  error={usersError}
                  clientId={client.id}
                  clientName={client.companyName}
                  tenantId={client.tenantId}
                  onUserUpdated={refetchUsers}
                  securitySnapshot={securitySnapshot}
                />
              )}
            </div>
          )}

          {/* Groups Tab */}
          {activeTab === 'groups' && (
            <div className="card p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-foreground">
                  Tenant Groups
                </h3>
                <button
                  onClick={() => refreshSyncData()}
                  className="text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400 flex items-center"
                >
                  <RefreshCw className="h-4 w-4 mr-1" />
                  Refresh
                </button>
              </div>
              {!client.tenantId ? (
                <div className="text-center py-8">
                  <UsersRound className="h-12 w-12 mx-auto text-muted-foreground" />
                  <p className="mt-2 text-muted-foreground">No Tenant ID configured</p>
                  <p className="text-sm text-muted-foreground">
                    Add a Microsoft Tenant ID to view groups
                  </p>
                </div>
              ) : (
                <GroupList
                  clientId={client.id}
                  syncData={syncData?.groups}
                  cachedUsers={syncData?.users?.data}
                  onRefreshNeeded={refreshSyncData}
                />
              )}
            </div>
          )}

          {/* Security Tab */}
          {activeTab === 'security' && (
            <div className="card p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <h3 className="text-lg font-medium text-foreground">
                    Security Overview
                  </h3>
                  {securitySnapshot?.collectedAt && (
                    <span className={`flex items-center text-xs px-2 py-1 rounded-full ${
                      differenceInHours(new Date(), parseISO(securitySnapshot.collectedAt)) > 48
                        ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300'
                        : 'bg-muted text-muted-foreground'
                    }`}>
                      <Clock className="h-3 w-3 mr-1" />
                      {differenceInHours(new Date(), parseISO(securitySnapshot.collectedAt)) > 48
                        ? `Stale data — collected ${format(parseISO(securitySnapshot.collectedAt), 'MMM d, h:mm a')}`
                        : `Data as of ${format(parseISO(securitySnapshot.collectedAt), 'MMM d, h:mm a')}`
                      }
                    </span>
                  )}
                </div>
                <button
                  onClick={handleRefreshSecurity}
                  disabled={securityRefreshing || securityLoading}
                  className="text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400 flex items-center disabled:opacity-50"
                >
                  <RefreshCw className={`h-4 w-4 mr-1 ${securityRefreshing || securityLoading ? 'animate-spin' : ''}`} />
                  {securityRefreshing ? 'Refreshing...' : 'Refresh Security'}
                </button>
              </div>

              {!client.tenantId ? (
                <div className="text-center py-8">
                  <ShieldCheck className="h-12 w-12 mx-auto text-muted-foreground" />
                  <p className="mt-2 text-muted-foreground">No Tenant ID configured</p>
                  <p className="text-sm text-muted-foreground">
                    Add a Microsoft Tenant ID to view security metrics
                  </p>
                </div>
              ) : securityLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                </div>
              ) : securityError ? (
                <div className="text-center py-8">
                  <ShieldCheck className="h-12 w-12 mx-auto text-muted-foreground" />
                  <p className="mt-2 text-muted-foreground">{securityError}</p>
                  <button
                    onClick={() => fetchSecuritySnapshot()}
                    className="mt-4 btn-primary"
                  >
                    Try Again
                  </button>
                </div>
              ) : securitySnapshot ? (
                <div className="space-y-6">
                  {/* Summary Cards */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {/* MFA Coverage */}
                    <div className={`rounded-lg p-4 ${
                      securitySnapshot.mfa.coverage >= 80
                        ? 'bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800'
                        : securitySnapshot.mfa.coverage >= 50
                        ? 'bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-800'
                        : 'bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800'
                    }`}>
                      <p className="text-xs font-medium text-muted-foreground uppercase">MFA Coverage</p>
                      <p className={`text-2xl font-bold mt-1 ${
                        securitySnapshot.mfa.coverage >= 80
                          ? 'text-green-700 dark:text-green-300'
                          : securitySnapshot.mfa.coverage >= 50
                          ? 'text-yellow-700 dark:text-yellow-300'
                          : 'text-red-700 dark:text-red-300'
                      }`}>
                        {securitySnapshot.mfa.coverage}%
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {securitySnapshot.mfa.mfaRegistered} of {securitySnapshot.mfa.totalUsers} users
                      </p>
                    </div>

                    {/* Secure Score */}
                    <div className={`rounded-lg p-4 ${
                      securitySnapshot.secureScore
                        ? securitySnapshot.secureScore.percentage >= 70
                          ? 'bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800'
                          : securitySnapshot.secureScore.percentage >= 50
                          ? 'bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-800'
                          : 'bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800'
                        : 'bg-muted border border-border'
                    }`}>
                      <p className="text-xs font-medium text-muted-foreground uppercase">Secure Score</p>
                      {securitySnapshot.secureScore ? (
                        <>
                          <p className={`text-2xl font-bold mt-1 ${
                            securitySnapshot.secureScore.percentage >= 70
                              ? 'text-green-700 dark:text-green-300'
                              : securitySnapshot.secureScore.percentage >= 50
                              ? 'text-yellow-700 dark:text-yellow-300'
                              : 'text-red-700 dark:text-red-300'
                          }`}>
                            {securitySnapshot.secureScore.percentage}%
                          </p>
                          <p className="text-xs text-muted-foreground mt-1">
                            {securitySnapshot.secureScore.current} / {securitySnapshot.secureScore.max}
                          </p>
                        </>
                      ) : (
                        <p className="text-lg font-medium mt-1 text-muted-foreground">N/A</p>
                      )}
                    </div>

                    {/* Admin Accounts */}
                    <div className={`rounded-lg p-4 ${
                      securitySnapshot.admins.withoutMfa === 0
                        ? 'bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800'
                        : 'bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800'
                    }`}>
                      <p className="text-xs font-medium text-muted-foreground uppercase">Admin Accounts</p>
                      <p className={`text-2xl font-bold mt-1 ${
                        securitySnapshot.admins.withoutMfa === 0
                          ? 'text-green-700 dark:text-green-300'
                          : 'text-red-700 dark:text-red-300'
                      }`}>
                        {securitySnapshot.admins.total}
                      </p>
                      <p className={`text-xs mt-1 ${
                        securitySnapshot.admins.withoutMfa > 0
                          ? 'text-red-600 dark:text-red-400 font-medium'
                          : 'text-muted-foreground'
                      }`}>
                        {securitySnapshot.admins.withoutMfa > 0
                          ? `${securitySnapshot.admins.withoutMfa} without MFA!`
                          : 'All have MFA'}
                      </p>
                    </div>

                    {/* Conditional Access */}
                    <div className="rounded-lg p-4 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800">
                      <p className="text-xs font-medium text-muted-foreground uppercase">CA Policies</p>
                      {securitySnapshot.conditionalAccess ? (
                        <>
                          <p className="text-2xl font-bold mt-1 text-blue-700 dark:text-blue-300">
                            {securitySnapshot.conditionalAccess.enabledPolicies}
                          </p>
                          <p className="text-xs text-muted-foreground mt-1">
                            of {securitySnapshot.conditionalAccess.totalPolicies} total enabled
                          </p>
                        </>
                      ) : (
                        <p className="text-lg font-medium mt-1 text-muted-foreground">N/A</p>
                      )}
                    </div>
                  </div>

                  {/* Admin List */}
                  {securitySnapshot.admins.list.length > 0 && (
                    <div>
                      <h4 className="text-sm font-medium text-foreground mb-3 flex items-center">
                        <Users className="h-4 w-4 mr-2" />
                        Admin Accounts ({securitySnapshot.admins.total})
                      </h4>
                      <div className="space-y-2">
                        {securitySnapshot.admins.list.map((admin, idx) => (
                          <div
                            key={idx}
                            className={`p-3 rounded-lg flex items-start justify-between ${
                              admin.mfaRegistered
                                ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800'
                                : 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'
                            }`}
                          >
                            <div>
                              <p className="font-medium text-foreground text-sm">
                                {admin.displayName}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                {admin.userPrincipalName}
                              </p>
                              <div className="flex flex-wrap gap-1 mt-1">
                                {admin.roles.map((role, roleIdx) => (
                                  <span
                                    key={roleIdx}
                                    className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-muted text-foreground"
                                  >
                                    {role}
                                  </span>
                                ))}
                              </div>
                            </div>
                            <span className={`flex-shrink-0 ml-2 px-2 py-1 rounded text-xs font-medium ${
                              admin.mfaRegistered
                                ? 'bg-green-100 dark:bg-green-800 text-green-700 dark:text-green-200'
                                : 'bg-red-100 dark:bg-red-800 text-red-700 dark:text-red-200'
                            }`}>
                              {admin.mfaRegistered ? 'MFA Enabled' : 'No MFA'}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Mailbox Security */}
                  {securitySnapshot.mailboxSecurity && (
                    <div>
                      <h4 className="text-sm font-medium text-foreground mb-3">
                        Mailbox Security
                      </h4>
                      <div className="grid grid-cols-3 gap-4">
                        <div className="text-center p-3 bg-muted rounded-lg">
                          <p className="text-2xl font-bold text-foreground">
                            {securitySnapshot.mailboxSecurity.totalMailboxes}
                          </p>
                          <p className="text-xs text-muted-foreground">Total Mailboxes</p>
                        </div>
                        <div className={`text-center p-3 rounded-lg ${
                          securitySnapshot.mailboxSecurity.externalForwardingEnabled > 0
                            ? 'bg-orange-50 dark:bg-orange-900/30'
                            : 'bg-green-50 dark:bg-green-900/30'
                        }`}>
                          <p className={`text-2xl font-bold ${
                            securitySnapshot.mailboxSecurity.externalForwardingEnabled > 0
                              ? 'text-orange-700 dark:text-orange-300'
                              : 'text-green-700 dark:text-green-300'
                          }`}>
                            {securitySnapshot.mailboxSecurity.externalForwardingEnabled}
                          </p>
                          <p className="text-xs text-muted-foreground">External Forwarding</p>
                        </div>
                        <div className={`text-center p-3 rounded-lg ${
                          securitySnapshot.mailboxSecurity.suspiciousRules > 0
                            ? 'bg-red-50 dark:bg-red-900/30'
                            : 'bg-green-50 dark:bg-green-900/30'
                        }`}>
                          <p className={`text-2xl font-bold ${
                            securitySnapshot.mailboxSecurity.suspiciousRules > 0
                              ? 'text-red-700 dark:text-red-300'
                              : 'text-green-700 dark:text-green-300'
                          }`}>
                            {securitySnapshot.mailboxSecurity.suspiciousRules}
                          </p>
                          <p className="text-xs text-muted-foreground">Suspicious Rules</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Last Updated */}
                  <div className="text-xs text-muted-foreground pt-4 border-t border-border">
                    Data collected: {format(parseISO(securitySnapshot.collectedAt), 'MMM d, yyyy h:mm a')}
                    {securitySnapshot.errors && securitySnapshot.errors.length > 0 && (
                      <span className="ml-2 text-orange-600 dark:text-orange-400">
                        ({securitySnapshot.errors.length} collection error{securitySnapshot.errors.length > 1 ? 's' : ''})
                      </span>
                    )}
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <ShieldCheck className="h-12 w-12 mx-auto text-muted-foreground" />
                  <p className="mt-2 text-muted-foreground">No security data available</p>
                  <button
                    onClick={() => fetchSecuritySnapshot()}
                    className="mt-4 btn-primary"
                  >
                    Load Security Data
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Mailboxes Tab */}
          {activeTab === 'mailboxes' && (
            <div className="card p-6">
              <div className="flex items-center gap-2 mb-4">
                <h3 className="text-lg font-medium text-foreground">
                  Mailboxes
                </h3>
                <HelpTooltip text="View all mailboxes (user, shared, room, equipment) and manage shared mailboxes for this client." size="sm" />
              </div>
              <MailboxesTab
                clientId={client.id}
                clientName={client.companyName}
                tenantId={client.tenantId}
                userMailboxes={syncData?.userMailboxes}
                sharedMailboxes={syncData?.sharedMailboxes}
                onRefreshNeeded={refreshSyncData}
              />
            </div>
          )}

          {/* SharePoint Tab */}
          {activeTab === 'sharepoint' && (
            <div className="card p-6">
              <div className="flex items-center gap-2 mb-4">
                <h3 className="text-lg font-medium text-foreground">
                  SharePoint Sites
                </h3>
                <HelpTooltip text="View SharePoint sites and manage site permissions. Permission changes use PowerShell runbooks and may take 30-60 seconds." size="sm" />
              </div>
              <SharePointSiteList
                clientId={client.id}
                clientName={client.companyName}
                tenantId={client.tenantId}
                syncData={syncData?.sharePointSites}
                sitePermissionsData={syncData?.sitePermissions}
                onRefreshNeeded={refreshSyncData}
              />
            </div>
          )}

          {/* SOC Tab */}
          {activeTab === 'soc' && client.tenantId && (
            <div className="space-y-6">
              <CollectionCoverage
                clientId={client.id}
                clientName={client.companyName}
                refreshTrigger={coverageRefreshTrigger}
              />
              <ClientSecurityDashboard
                clientId={client.id}
                clientName={client.companyName}
                onCollectionComplete={() => setCoverageRefreshTrigger(prev => prev + 1)}
              />
            </div>
          )}

          {/* Email Health Tab */}
          {activeTab === 'emailhealth' && client.tenantId && (
            <div className="space-y-6">
              <div className="card p-6">
                <ClientEmailHealth
                  clientId={client.id}
                  clientName={client.companyName}
                />
              </div>
              <div className="card p-6">
                <SpamSamplesPanel
                  clientId={client.id}
                  clientName={client.companyName}
                />
              </div>
            </div>
          )}

          {/* DMARC Tab */}
          {activeTab === 'dmarc' && client.tenantId && (
            <ClientDmarc clientId={client.id} clientName={client.companyName} />
          )}

          {/* Google Workspace Tabs */}
          {activeTab === 'google-oauth' && (
            <GoogleOAuthApps apps={syncData?.googleOAuthApps?.data || []} />
          )}

          {activeTab === 'google-devices' && (
            <GoogleMobileDevices devices={syncData?.googleMobileDevices?.data || []} />
          )}

          {activeTab === 'google-audit' && (
            <GoogleAdminAudit events={syncData?.googleAdminAudit?.data || []} />
          )}
        </div>

        {/* Sidebar Toggle - Vertical edge tab when collapsed (desktop only) */}
        {sidebarCollapsed && (
          <div
            className="hidden lg:flex fixed right-0 z-40 items-center select-none"
            style={{
              top: `${sidebarTabY}px`,
              writingMode: 'vertical-rl',
              transform: 'rotate(180deg)',
              padding: '12px 6px',
              background: 'var(--card)',
              border: '1px solid var(--border)',
              borderRight: 'none',
              borderRadius: '8px 0 0 8px',
              cursor: isDraggingTab ? 'grabbing' : 'grab',
              fontSize: '12px',
              fontWeight: 600,
              letterSpacing: '0.05em',
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
              userSelect: 'none',
              transition: isDraggingTab ? 'none' : 'box-shadow 0.2s',
            }}
            onMouseDown={handleTabMouseDown}
            title="Click to show sidebar / Drag to reposition"
          >
            <span className="text-muted-foreground hover:text-foreground transition-colors">Sidebar</span>
          </div>
        )}

        {/* Sidebar - fixed on desktop, stacked on mobile */}
        <div
          className={`lg:fixed lg:top-0 lg:right-0 lg:h-full lg:w-[340px] lg:overflow-y-auto lg:z-20 lg:bg-background lg:pt-20 lg:pb-6 lg:px-4 transition-transform duration-300 ease-in-out ${sidebarCollapsed ? 'lg:translate-x-full' : 'lg:translate-x-0'}`}
        >
        {/* Sidebar collapse button inside sidebar header (desktop only) */}
        <div className="hidden lg:flex justify-end mb-2">
          <button
            onClick={toggleSidebar}
            className="flex items-center gap-1.5 px-2 py-1 text-xs text-muted-foreground hover:text-foreground hover:bg-accent rounded transition-colors"
            title="Hide sidebar"
          >
            <span>Hide</span>
            <ChevronRight className="h-3.5 w-3.5" />
          </button>
        </div>
        <div className="space-y-6">
          {/* GDAP Relationship Status */}
          <div className={`card p-6 ${
            gdapStatus?.status === 'active' ? 'border-l-4 border-green-500' :
            gdapStatus?.status === 'approved' || gdapStatus?.status === 'approvalPending' ? 'border-l-4 border-yellow-500' :
            gdapStatus?.status === 'terminated' || gdapStatus?.status === 'expiring' ? 'border-l-4 border-red-500' :
            gdapStatus?.status === 'none' || gdapStatus?.status === 'no-tenant-id' ? 'border-l-4 border-muted-foreground' :
            ''
          }`}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-medium text-foreground flex items-center">
                <Link2 className="h-5 w-5 mr-2 text-muted-foreground" />
                GDAP Relationship
              </h3>
              {!gdapLoading && gdapStatus?.hasRelationship && gdapStatus.endDateTime && (
                <span className="text-xs text-muted-foreground">
                  Expires {format(parseISO(gdapStatus.endDateTime), 'MMM d, yyyy')}
                </span>
              )}
            </div>

            {gdapLoading ? (
              <div className="flex items-center justify-center py-4">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
              </div>
            ) : gdapStatus ? (
              <div className="space-y-2">
                {/* Status indicator */}
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Status</span>
                  <span className={`font-medium flex items-center gap-1 ${
                    gdapStatus.status === 'active' ? 'text-green-600 dark:text-green-400' :
                    gdapStatus.status === 'approved' ? 'text-blue-600 dark:text-blue-400' :
                    gdapStatus.status === 'approvalPending' ? 'text-yellow-600 dark:text-yellow-400' :
                    gdapStatus.status === 'terminated' || gdapStatus.status === 'expiring' ? 'text-red-600 dark:text-red-400' :
                    'text-muted-foreground'
                  }`}>
                    {gdapStatus.status === 'active' && <CheckCircle className="h-4 w-4" />}
                    {gdapStatus.status === 'approved' && <CheckCircle className="h-4 w-4" />}
                    {gdapStatus.status === 'approvalPending' && <Calendar className="h-4 w-4" />}
                    {(gdapStatus.status === 'terminated' || gdapStatus.status === 'expiring') && <XCircle className="h-4 w-4" />}
                    {gdapStatus.status === 'none' && <AlertTriangle className="h-4 w-4" />}
                    {gdapStatus.status === 'no-tenant-id' && <AlertTriangle className="h-4 w-4" />}
                    {gdapStatus.status === 'active' ? 'Active' :
                     gdapStatus.status === 'approved' ? 'Approved' :
                     gdapStatus.status === 'approvalPending' ? 'Pending Approval' :
                     gdapStatus.status === 'expiring' ? 'Expiring' :
                     gdapStatus.status === 'terminated' ? 'Terminated' :
                     gdapStatus.status === 'no-tenant-id' ? 'No Tenant ID' :
                     gdapStatus.status === 'none' ? 'No Relationship' :
                     gdapStatus.status}
                  </span>
                </div>

                {/* Role count */}
                {gdapStatus.roleCount !== undefined && gdapStatus.roleCount > 0 && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Admin Roles</span>
                    <span className="font-medium text-foreground">{gdapStatus.roleCount}</span>
                  </div>
                )}

                {/* Multiple relationships indicator */}
                {gdapStatus.relationships && gdapStatus.relationships.length > 1 && (
                  <div className="text-xs text-muted-foreground pt-1">
                    {gdapStatus.relationships.length} relationships found (showing best)
                  </div>
                )}

                {/* Action buttons */}
                {(gdapStatus.status === 'none' || gdapStatus.status === 'terminated' || gdapStatus.status === 'no-tenant-id') && (
                  <button
                    onClick={() => navigate(`/clients/onboard?clientId=${id}&clientName=${encodeURIComponent(client.companyName)}${client.tenantId ? `&tenantId=${client.tenantId}` : ''}${client.tenantDomain ? `&tenantDomain=${encodeURIComponent(client.tenantDomain)}` : ''}`)}
                    className="w-full mt-2 py-1.5 px-3 text-xs font-medium text-white bg-primary hover:bg-primary/90 rounded-md transition-colors"
                  >
                    {gdapStatus.status === 'terminated' ? 'Create New GDAP Relationship' :
                     gdapStatus.status === 'no-tenant-id' ? 'Set Up GDAP' :
                     'Start GDAP Onboarding'}
                  </button>
                )}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                Unable to check GDAP status
              </p>
            )}
          </div>

          {/* GDAP Consent Status */}
          {client.tenantId && (
            <div className={`card p-6 ${
              consentStatus?.allConsentsGranted
                ? 'border-l-4 border-green-500'
                : consentStatus && !consentStatus.allConsentsGranted
                ? 'border-l-4 border-orange-500'
                : ''
            }`}>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-medium text-foreground flex items-center">
                  <ShieldCheck className="h-5 w-5 mr-2 text-muted-foreground" />
                  GDAP Consents
                </h3>
                <button
                  onClick={() => {
                    fetchConsentStatus();
                    setShowConsentWizard(true);
                  }}
                  className="text-xs text-primary-600 hover:text-primary-700 dark:text-primary-400"
                >
                  Manage
                </button>
              </div>

              {consentLoading ? (
                <div className="flex items-center justify-center py-4">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
                </div>
              ) : consentStatus ? (
                <div className="space-y-2">
                  {/* Progress - 4 steps: App Consent (with password reset + TAP policy + group member), Graph CLI, Exchange Role, OverSite */}
                  {(() => {
                    // App Consent is only fully complete if password reset, TAP policy, AND group member permissions are granted
                    const appConsentComplete = consentStatus.appConsent.granted &&
                      consentStatus.passwordResetPermission?.granted &&
                      consentStatus.tapPolicyPermission?.granted &&
                      consentStatus.groupMemberPermission?.granted;
                    const completedSteps =
                      (appConsentComplete ? 1 : 0) +
                      (consentStatus.graphCliConsent.granted ? 1 : 0) +
                      (consentStatus.exchangeRole.assigned ? 1 : 0) +
                      (consentStatus.oversiteConsent?.granted ? 1 : 0);
                    const allComplete = completedSteps === 4;
                    return (
                      <>
                        <div className="flex items-center justify-between text-sm mb-2">
                          <span className="text-muted-foreground">Status</span>
                          <span className={`font-medium ${
                            allComplete
                              ? 'text-green-600 dark:text-green-400'
                              : 'text-orange-600 dark:text-orange-400'
                          }`}>
                            {completedSteps} of 4 complete
                          </span>
                        </div>

                        {/* Progress bar */}
                        <div className="h-2 rounded-full overflow-hidden bg-muted">
                          <div
                            className={`h-full transition-all ${allComplete ? 'bg-green-500' : 'bg-orange-500'}`}
                            style={{ width: `${(completedSteps / 4) * 100}%` }}
                          />
                        </div>
                      </>
                    );
                  })()}

                  {/* Individual consents - 3 clear steps */}
                  <div className="pt-2 space-y-1.5 text-xs">
                    {/* Step 1: App Consent (includes password reset + TAP policy + group member permissions) */}
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">1. App Consent</span>
                      {consentStatus.appConsent.granted && consentStatus.passwordResetPermission?.granted && consentStatus.tapPolicyPermission?.granted && consentStatus.groupMemberPermission?.granted ? (
                        <span className="text-green-600 dark:text-green-400 flex items-center">
                          <CheckCircle className="h-4 w-4 mr-1" />
                          Done
                        </span>
                      ) : consentStatus.appConsent.granted ? (
                        <button
                          onClick={() => handleGrantAppConsent(true)}
                          className="text-xs px-2 py-0.5 bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300 rounded hover:bg-yellow-200 dark:hover:bg-yellow-800 transition-colors"
                          title="App consented but missing permissions (TAP policy or group member) - click to update consent"
                        >
                          Needs Update
                        </button>
                      ) : (
                        <span className="text-orange-600 dark:text-orange-400">Missing</span>
                      )}
                    </div>

                    {/* Step 2: Graph CLI */}
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">2. Graph CLI</span>
                      {consentStatus.graphCliConsent.granted ? (
                        <span className="text-green-600 dark:text-green-400 flex items-center">
                          <CheckCircle className="h-4 w-4 mr-1" />
                          Done
                        </span>
                      ) : (
                        <span className="text-orange-600 dark:text-orange-400">Missing</span>
                      )}
                    </div>

                    {/* Step 3: Exchange Role */}
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">3. Exchange Role</span>
                      {consentStatus.exchangeRole.assigned ? (
                        <span className="text-green-600 dark:text-green-400 flex items-center">
                          <CheckCircle className="h-4 w-4 mr-1" />
                          Done
                        </span>
                      ) : (
                        <span className="text-orange-600 dark:text-orange-400">Missing</span>
                      )}
                    </div>

                    {/* Step 4: OverSite */}
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">4. OverSite</span>
                      {consentStatus.oversiteConsent?.granted ? (
                        <span className="text-green-600 dark:text-green-400 flex items-center">
                          <CheckCircle className="h-4 w-4 mr-1" />
                          Done
                        </span>
                      ) : (
                        <span className="text-orange-600 dark:text-orange-400">Missing</span>
                      )}
                    </div>
                  </div>

                  {!(consentStatus.appConsent.granted && consentStatus.passwordResetPermission?.granted && consentStatus.tapPolicyPermission?.granted && consentStatus.groupMemberPermission?.granted && consentStatus.graphCliConsent.granted && consentStatus.exchangeRole.assigned && consentStatus.oversiteConsent?.granted) && (
                    <button
                      onClick={() => setShowConsentWizard(true)}
                      className="w-full mt-2 py-1.5 px-3 text-xs font-medium text-white bg-orange-500 hover:bg-orange-600 rounded-md transition-colors"
                    >
                      Grant Missing Consents
                    </button>
                  )}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">
                  Click "Manage" to check consent status
                </p>
              )}
            </div>
          )}

          {/* License Overview */}
          <div className="card p-6">
            <div className="flex items-center gap-2 mb-4">
              <h3 className="text-lg font-medium text-foreground">
                License Overview
              </h3>
              <HelpTooltip text="Summary of license counts and utilization. Shows total MS tenant licenses, how many are covered by Pax8, and how many are actually assigned to users." size="sm" />
            </div>

            {licenseAnalytics && (
              <div className="space-y-4">
                {/* MS Tenant Licenses */}
                <div>
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-muted-foreground">MS Tenant Licenses</span>
                    <span className="font-medium text-foreground">
                      {licenseAnalytics.msTenantLicenses.toLocaleString()}
                    </span>
                  </div>
                  {/* Coverage bar: Pax8 covered vs unmatched */}
                  {licenseAnalytics.msTenantLicenses > 0 && (
                    <>
                      <div className="h-4 rounded-full overflow-hidden bg-muted flex">
                        {licenseAnalytics.pax8Coverage > 0 && (
                          <div
                            className="bg-green-500 h-full"
                            style={{ width: `${Math.min(100, (licenseAnalytics.pax8Coverage / licenseAnalytics.msTenantLicenses) * 100)}%` }}
                            title={`Pax8 Covered: ${licenseAnalytics.pax8Coverage}`}
                          />
                        )}
                        {licenseAnalytics.unmatchedLicenses > 0 && (
                          <div
                            className="bg-orange-500 h-full"
                            style={{ width: `${(licenseAnalytics.unmatchedLicenses / licenseAnalytics.msTenantLicenses) * 100}%` }}
                            title={`Unmatched: ${licenseAnalytics.unmatchedLicenses}`}
                          />
                        )}
                      </div>
                      <div className="flex justify-between text-xs mt-1">
                        <span className="text-green-600 dark:text-green-400">
                          Pax8 Covered: {licenseAnalytics.pax8Coverage.toLocaleString()}
                        </span>
                        <span className="text-orange-600 dark:text-orange-400">
                          Unmatched: {licenseAnalytics.unmatchedLicenses.toLocaleString()}
                        </span>
                      </div>
                    </>
                  )}
                </div>

                {/* Utilization */}
                <div className="pt-3 border-t border-border">
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-muted-foreground">License Usage</span>
                    <span className="font-medium text-foreground">
                      {licenseAnalytics.msTenantLicenses > 0
                        ? `${licenseAnalytics.msTenantAssigned.toLocaleString()} / ${licenseAnalytics.msTenantLicenses.toLocaleString()} (${licenseAnalytics.utilizationPercent}%)`
                        : 'N/A'}
                    </span>
                  </div>
                  {licenseAnalytics.msTenantLicenses > 0 && (
                    <div className="h-2 rounded-full overflow-hidden bg-muted">
                      <div
                        className={`h-full ${
                          licenseAnalytics.utilizationPercent >= 90 ? 'bg-red-500' :
                          licenseAnalytics.utilizationPercent >= 70 ? 'bg-yellow-500' :
                          'bg-blue-500'
                        }`}
                        style={{ width: `${Math.min(100, licenseAnalytics.utilizationPercent)}%` }}
                      />
                    </div>
                  )}
                  {/* Available Licenses */}
                  {licenseAnalytics.msTenantLicenses > 0 && (licenseAnalytics.msTenantLicenses - licenseAnalytics.msTenantAssigned) > 0 && (
                    <div className="flex justify-between text-sm mt-2">
                      <span className="text-blue-600 dark:text-blue-400">Available Licenses</span>
                      <span className="font-medium text-blue-600 dark:text-blue-400">
                        {(licenseAnalytics.msTenantLicenses - licenseAnalytics.msTenantAssigned).toLocaleString()}
                      </span>
                    </div>
                  )}
                </div>

                {/* Quick Stats */}
                <div className="pt-3 border-t border-border space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Product Groups</span>
                    <span className="font-medium text-foreground">
                      {groupedProducts.length}
                    </span>
                  </div>
                  {licenseAnalytics.orphanedCount > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-red-600 dark:text-red-400">Orphaned Records</span>
                      <span className="font-medium text-red-600 dark:text-red-400">
                        {licenseAnalytics.orphanedCount} ({licenseAnalytics.orphanedLicenses} licenses)
                      </span>
                    </div>
                  )}
                </div>

                {/* Last Synced */}
                <div className="pt-3 border-t border-border">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Last Synced</span>
                    <span className="text-foreground">
                      {(() => {
                        const lastSync = client.subscriptions
                          ?.filter(s => s.lastSyncDate)
                          .map(s => s.lastSyncDate)
                          .sort()
                          .reverse()[0];
                        return lastSync
                          ? format(parseISO(lastSync), 'MMM d, h:mm a')
                          : 'Never';
                      })()}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Billing Status */}
          {client.subscriptions && (() => {
            const subsWithBilling = client.subscriptions.filter(s => s.billingLastChecked);
            const billingWarnings = client.subscriptions.filter(s => s.msBillingStatus === 'warning');
            const lastBillingCheck = subsWithBilling
              .map(s => s.billingLastChecked!)
              .sort()
              .reverse()[0];

            if (subsWithBilling.length === 0 && !client.tenantId) return null;

            return (
              <div className="card p-6">
                <h3 className="text-lg font-medium text-foreground mb-4 flex items-center">
                  <DollarSign className="h-5 w-5 mr-2 text-muted-foreground" />
                  Billing Status
                </h3>

                <div className="space-y-3">
                  {lastBillingCheck ? (
                    <>
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Last Verified</span>
                        <span className="text-foreground">
                          {format(parseISO(lastBillingCheck), 'MMM d, h:mm a')}
                        </span>
                      </div>

                      {billingWarnings.length > 0 ? (
                        <div className="mt-2 p-2 bg-orange-50 dark:bg-orange-900/30 rounded-lg border border-orange-200 dark:border-orange-800">
                          <p className="text-sm font-medium text-orange-800 dark:text-orange-200 flex items-center">
                            <AlertTriangle className="h-4 w-4 mr-1" />
                            {billingWarnings.length} subscription{billingWarnings.length > 1 ? 's' : ''} with warnings
                          </p>
                          <p className="text-xs text-orange-600 dark:text-orange-400 mt-1">
                            These may not be set to auto-renew in MS billing
                          </p>
                          <ul className="mt-2 space-y-1">
                            {billingWarnings.slice(0, 3).map(sub => (
                              <li key={sub.id} className="text-xs text-orange-700 dark:text-orange-300 truncate">
                                {sub.productName}
                              </li>
                            ))}
                            {billingWarnings.length > 3 && (
                              <li className="text-xs text-orange-600 dark:text-orange-400">
                                +{billingWarnings.length - 3} more
                              </li>
                            )}
                          </ul>
                        </div>
                      ) : (
                        <div className="mt-2 p-2 bg-green-50 dark:bg-green-900/30 rounded-lg border border-green-200 dark:border-green-800">
                          <p className="text-sm text-green-700 dark:text-green-300">
                            No billing warnings detected
                          </p>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="text-sm text-muted-foreground">
                      <p>Billing not yet verified</p>
                      <p className="text-xs mt-1">
                        Click "Verify Billing" button above to check MS subscription renewal status
                      </p>
                    </div>
                  )}
                </div>
              </div>
            );
          })()}

          {/* License Gap Warnings */}
          {licenseGaps.length > 0 && (
            <div className="card p-6 border-l-4 border-orange-500">
              <h3 className="text-lg font-medium text-foreground mb-3 flex items-center">
                <ShoppingCart className="h-5 w-5 mr-2 text-orange-500" />
                Order from Pax8
              </h3>
              <p className="text-xs text-muted-foreground mb-3">
                MS licenses expiring without Pax8 coverage
              </p>
              <div className="space-y-2">
                {licenseGaps.map((gap, idx) => (
                  <div
                    key={idx}
                    className={`p-2 rounded text-xs ${
                      gap.severity === 'hard'
                        ? 'bg-red-100 dark:bg-red-900/50 border border-red-300 dark:border-red-700'
                        : gap.severity === 'medium'
                        ? 'bg-orange-100 dark:bg-orange-900/50 border border-orange-300 dark:border-orange-700'
                        : 'bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-300 dark:border-yellow-700'
                    }`}
                  >
                    <div className="flex items-start">
                      <AlertTriangle className={`h-4 w-4 mr-1 flex-shrink-0 ${
                        gap.severity === 'hard'
                          ? 'text-red-600 dark:text-red-400'
                          : gap.severity === 'medium'
                          ? 'text-orange-600 dark:text-orange-400'
                          : 'text-yellow-600 dark:text-yellow-500'
                      }`} />
                      <div className="min-w-0 flex-1">
                        <p className={`font-medium truncate ${
                          gap.severity === 'hard'
                            ? 'text-red-800 dark:text-red-200'
                            : gap.severity === 'medium'
                            ? 'text-orange-800 dark:text-orange-200'
                            : 'text-yellow-800 dark:text-yellow-200'
                        }`} title={gap.product}>
                          {gap.product}
                        </p>
                        <p className={`${
                          gap.severity === 'hard'
                            ? 'text-red-700 dark:text-red-300'
                            : gap.severity === 'medium'
                            ? 'text-orange-700 dark:text-orange-300'
                            : 'text-yellow-700 dark:text-yellow-300'
                        }`}>
                          Need {gap.gap} licenses in {gap.daysUntilExpiry} days
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Contacts */}
          {client.contacts && client.contacts.length > 0 && (
            <div className="card p-6">
              <h3 className="text-lg font-medium text-foreground mb-4">
                Contacts
              </h3>
              <div className="space-y-3">
                {client.contacts.map((contact, index) => (
                  <div key={index} className="text-sm">
                    <p className="font-medium text-foreground">
                      {contact.name}
                    </p>
                    {contact.email && (
                      <p className="text-muted-foreground">
                        {contact.email}
                      </p>
                    )}
                    {contact.phone && (
                      <p className="text-muted-foreground">
                        {contact.phone}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        </div>
      </div>

      {/* Edit Modal */}
      {showEditForm && (
        <ClientForm
          client={client}
          onClose={() => setShowEditForm(false)}
          onSuccess={() => {
            setShowEditForm(false);
            refetch();
          }}
        />
      )}

      {/* Consent Wizard Modal */}
      {showConsentWizard && consentStatus && (
        <ConsentWizard
          isOpen={showConsentWizard}
          onClose={() => setShowConsentWizard(false)}
          consentStatus={consentStatus}
          clientId={id || ''}
          onRefresh={refetchConsentStatus}
        />
      )}

      {/* License Assignment Report Modal */}
      <LicenseAssignmentReport
        isOpen={showLicenseReport}
        onClose={() => setShowLicenseReport(false)}
        clientId={client.id}
        clientName={client.companyName}
        users={users}
        subscriptions={client.subscriptions || []}
      />

      {/* Bulk License Assignment Modal */}
      {bulkAssignModal && (
        <BulkLicenseAssignModal
          isOpen={bulkAssignModal.isOpen}
          onClose={() => setBulkAssignModal(null)}
          clientId={client.id}
          clientName={client.companyName}
          skuId={bulkAssignModal.skuId}
          productName={bulkAssignModal.productName}
          availableLicenses={bulkAssignModal.available}
          onAssigned={() => {
            // Refresh client data to update license counts
            refetch();
            // Also trigger MS sync to update user license data
            triggerMSSync();
          }}
        />
      )}
    </div>
  );
}
